import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl, getApiBaseUrl } from './api';
import { formatMicroUsdt } from './usdtFormat';
import { orderRefLabel } from './orderRef';
import { orderCategoryLabel } from './categoryLabels';
import { orderStatusLabel } from './orderStatusLabels';
import './App.css';

const STORAGE_KEY = 'freelancyx_admin_secret';

/** Как в fetchStats — иначе X-Admin-Secret может не совпасть с «чистым» ключом. */
function normalizeAdminSecret(raw: string): string {
  return raw.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
}

type JwtPayload = { telegram_id?: number | string; exp?: number };

function jwtPayload(token: string): JwtPayload | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function telegramIdOf(token: string): string {
  const id = jwtPayload(token)?.telegram_id;
  if (id == null || id === '') return '';
  return String(id);
}

function secretStorageKey(tgId: string): string {
  return `${STORAGE_KEY}:${tgId}`;
}

/** Вход мини-приложения. Кладётся в заголовок админ-запросов, чтобы сервер сверил Telegram-аккаунт. */
let ownerBearer = '';

function adminFetch(input: RequestInfo | URL, init?: RequestInit) {
  const headers = new Headers(init?.headers);
  if (ownerBearer) headers.set('Authorization', `Bearer ${ownerBearer}`);
  return globalThis.fetch(input, { ...init, headers });
}

function readStoredUserToken(): string {
  try {
    return localStorage.getItem('token') || '';
  } catch {
    return '';
  }
}

async function ensureOwnerToken(): Promise<string> {
  const existing = readStoredUserToken();
  const payload = existing ? jwtPayload(existing) : null;
  const fresh = !!payload?.exp && payload.exp * 1000 > Date.now() + 60_000 && !!payload.telegram_id;
  if (existing && fresh) return existing;
  const initData = String((window as unknown as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp?.initData || '');
  if (!initData) return existing;
  try {
    const res = await globalThis.fetch(apiUrl('/api/auth/telegram'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData })
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data && typeof data.token === 'string' && data.token) {
      try {
        localStorage.setItem('token', data.token);
      } catch {
        // память телефона недоступна — запрос всё равно уйдёт с токеном в этой сессии
      }
      return data.token;
    }
  } catch {
    // без сети остаётся старый токен, если он был
  }
  return existing;
}

function rememberAdminSecret(tgId: string, secret: string) {
  if (!tgId) return;
  try {
    localStorage.setItem(secretStorageKey(tgId), secret);
  } catch {
    // ignore
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function forgetAdminSecret(tgId: string) {
  try {
    if (tgId) localStorage.removeItem(secretStorageKey(tgId));
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Тема админки хранится отдельно от темы основного мини-аппа (fcyx_palette_theme) — без привязки к бирже. */
const ADMIN_THEME_KEY = 'fcyx_admin_palette_theme';

type AdminPaletteTheme = 'sun' | 'aurora' | 'mint' | 'midnight' | 'fog';

const ADMIN_THEMES: Array<{ id: AdminPaletteTheme; label: string }> = [
  { id: 'sun', label: 'Лето' },
  { id: 'aurora', label: 'Аврора' },
  { id: 'mint', label: 'Мята' },
  { id: 'midnight', label: 'Полночь' },
  { id: 'fog', label: 'Туман' }
];

function readStoredAdminTheme(): AdminPaletteTheme {
  try {
    const raw = window.localStorage.getItem(ADMIN_THEME_KEY);
    if (raw && ADMIN_THEMES.some((t) => t.id === raw)) {
      return raw as AdminPaletteTheme;
    }
  } catch {
    // localStorage недоступен — используем тему по умолчанию
  }
  return 'sun';
}

type FinanceUsdt = {
  platform_treasury_balance?: number;
  commission_earned_lifetime?: number;
  commission_withdrawn_recorded?: number;
  usdt_in_user_balances_total?: number;
  approximate_total_deposits?: number;
  withdrawal_completed?: number;
  withdrawal_pending?: number;
  escrow_orders_budget?: number;
  net_profit_platform?: number;
  deposits_current_month?: number;
  user_withdrawals_current_month?: number;
  platform_withdrawals_current_month?: number;
  cryptopay_app_available?: number | null;
};

/** Поля finance с микро-суммами и строками Crypto Pay */
type FinanceRecord = Record<string, unknown> & {
  usdt?: FinanceUsdt;
  stars?: {
    earned_lifetime?: number;
    earned_current_month?: number;
    payments_count?: number;
    payments_current_month?: number;
    by_sku?: Record<string, { stars?: number; payments?: number }>;
    telegram_bot_balance?: number | null;
    telegram_inbound_lifetime?: number | null;
    telegram_inbound_month?: number | null;
    telegram_payments_month?: number | null;
    telegram_error?: string | null;
  };
  stars_telegram_bot_balance?: number | null;
  stars_telegram_inbound_lifetime?: number | null;
  stars_telegram_inbound_month?: number | null;
  stars_telegram_payments_month?: number | null;
  stars_telegram_error?: string | null;
  stars_earned_lifetime?: number;
  stars_earned_current_month?: number;
  stars_payments_count?: number;
  stars_payments_current_month?: number;
  cryptopay_balance_error?: string | null;
  net_profit_platform_usdt_micro?: number;
  escrow_orders_budget_usdt_micro?: number;
  deposits_current_month_usdt_micro?: number;
  user_withdrawals_current_month_usdt_micro?: number;
  platform_withdrawals_current_month_usdt_micro?: number;
};

type MonthlyArchiveRow = {
  label: string;
  year: number;
  month: number;
  deposits_usdt?: number;
  user_withdrawals_usdt?: number;
  platform_withdrawals_usdt?: number;
  commission_usdt?: number;
  net_platform_usdt?: number;
};

type Stats = {
  success?: boolean;
  generated_at?: string;
  config?: { platform_commission_percent: number; platform_telegram_id: number };
  users?: Record<string, number>;
  orders?: Record<string, unknown>;
  finance?: FinanceRecord;
  moderation?: Record<string, number>;
  error?: string;
  detail?: string;
  code?: string;
};

type WithdrawalRequest = {
  id: number;
  user_id: number;
  amount_usdt_micro: number;
  amount_stars?: number;
  status: string;
  admin_note?: string | null;
  created_at: string;
  processed_at?: string | null;
  telegram_id: string | number;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

type AdminOrderRow = {
  id: number;
  status: string;
  title: string | null;
  category: string | null;
  budget: number;
  is_urgent?: boolean;
  is_promo_free?: boolean;
  created_at: string;
  customer_id: number;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  customer_username?: string | null;
  customer_telegram_id?: string | number | null;
  responses_pending_count: number;
  responses_total_count: number;
};

type AdminUserRow = {
  id: number;
  telegram_id: string | number | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
  created_at?: string | null;
  exchange_registered_at?: string | null;
  last_seen_at?: string | null;
  rating?: number | null;
  total_ratings?: number | null;
  trust_score?: number | null;
  is_vip?: boolean | null;
  vip_expires_at?: string | null;
  is_banned?: boolean | null;
  is_deleted?: boolean | null;
  ban_reason?: string | null;
  balance_usdt_micro?: number | string | null;
  active_orders_count?: number | null;
  active_orders_budget_micro?: number | string | null;
  active_services_count?: number | null;
  active_services_price_micro?: number | string | null;
  deposited_usdt_micro?: number | string | null;
  withdrawn_usdt_micro?: number | string | null;
  signup_ip?: string | null;
  signup_ip_at?: string | null;
  last_ip?: string | null;
  last_ip_at?: string | null;
  executor_vip_until?: string | null;
  executor_top_until?: string | null;
  urgent_slots_left?: number | null;
  executor_service_channel_post_credits?: number | null;
  executor_service_channel_posts_remaining?: number | null;
};

function adminRoleLabelRu(role: string | null | undefined): string {
  const r = String(role || '').trim();
  if (r === 'customer') return 'Заказчик';
  if (r === 'executor') return 'Исполнитель';
  if (r === 'pending') return 'Ожидает';
  return r || '—';
}

function adminAccountStatusRu(u: Pick<AdminUserRow, 'is_banned' | 'is_deleted'>): string {
  if (u.is_banned) return 'Забанен';
  if (u.is_deleted) return 'Анкета удалена';
  return 'Активен';
}

type AdminReportRow = {
  id: number;
  reporter_id: number;
  reported_user_id: number;
  order_id: number | null;
  reason: string;
  status?: string;
  created_at: string;
  reporter_first_name?: string | null;
  reporter_last_name?: string | null;
  reporter_username?: string | null;
  reporter_role?: string | null;
  reported_first_name?: string | null;
  reported_last_name?: string | null;
  reported_username?: string | null;
  reported_role?: string | null;
  kind?: string | null;
};

type DisputeParty = {
  id: number;
  telegram_id?: string | number | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  rating?: number;
  trust_score?: number;
  score_balance?: number;
  is_banned?: boolean;
  ban_reason?: string | null;
};

type DisputeMessage = {
  id: number;
  body: string;
  created_at: string;
  sender_id: number;
  sender_telegram_id?: string | number | null;
  sender_username?: string | null;
  sender_first_name?: string | null;
  sender_last_name?: string | null;
};

function formatFinanceUsdt(
  finance: FinanceRecord | undefined,
  key: keyof FinanceUsdt,
  microFallback: number
): string {
  const u = finance?.usdt;
  const v = u?.[key];
  if (v != null && typeof v === 'number' && Number.isFinite(v)) {
    return `${v} USDT`;
  }
  return `${formatMicroUsdt(microFallback)} USDT`;
}

/** Число или FRL-0029 / #FRL-0029 */
function parseAdminOrderId(raw: string): number | null {
  const s = String(raw || '')
    .trim()
    .replace(/^#/, '');
  const m = /^FRL-0*(\d+)$/i.exec(s);
  if (m) return parseInt(m[1], 10);
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? null : n;
}

function StatRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '6px 0',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        fontSize: '14px'
      }}
    >
      <span style={{ color: 'var(--fc-text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--fc-text)', fontWeight: 700 }}>{value}</span>
    </div>
  );
}

export default function AdminPage() {
  type AdminTab = 'dashboard' | 'orders' | 'users' | 'promo' | 'sources' | 'mail';
  const [secret, setSecret] = useState('');
  const [input, setInput] = useState('');
  const [stats, setStats] = useState<Stats | null>(null);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  type PromoSlideAdmin = {
    id: number;
    title: string;
    subtitle: string | null;
    tone: string;
    audience: string;
    image_url: string | null;
    show_telegram_mark: boolean;
    actions: Array<{ label: string; cta: { type: string; url?: string } }>;
    sort_order: number;
    is_active: boolean;
    duration_days: number | null;
    starts_at: string | null;
    ends_at: string | null;
    ends_in_ms?: number | null;
    click_count?: number;
    unique_click_count?: number;
  };
  const emptyPromoForm = () => ({
    title: '',
    subtitle: '',
    tone: 'amber',
    audience: 'all',
    image_url: '',
    show_telegram_mark: false,
    sort_order: '0',
    is_active: true,
    duration_days: '7',
    btn1_label: 'Перейти',
    btn1_type: 'external',
    btn1_url: '',
    btn2_label: '',
    btn2_type: 'none',
    btn2_url: ''
  });
  const [promoSlides, setPromoSlides] = useState<PromoSlideAdmin[]>([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoErr, setPromoErr] = useState<string | null>(null);
  const [promoEditId, setPromoEditId] = useState<number | null>(null);
  const [promoForm, setPromoForm] = useState(emptyPromoForm);
  const [promoImageSpec, setPromoImageSpec] = useState<{
    width?: number;
    height?: number;
    max_mb?: number;
    hint_ru?: string;
  } | null>(null);
  const [promoNowTs, setPromoNowTs] = useState(() => Date.now());

  type AcquisitionRow = {
    source_code: string;
    opened: number;
    already: number;
    registered: number;
    pending: number;
    customers: number;
    executors: number;
    link: string;
  };
  const [acquisitionRows, setAcquisitionRows] = useState<AcquisitionRow[]>([]);
  const [acquisitionBot, setAcquisitionBot] = useState('freelancyxbot');
  const [acquisitionLoading, setAcquisitionLoading] = useState(false);
  const [acquisitionErr, setAcquisitionErr] = useState<string | null>(null);
  const [mailUnfinishedText, setMailUnfinishedText] = useState('');
  const [mailRegisteredText, setMailRegisteredText] = useState('');
  const [mailAudience, setMailAudience] = useState<'all' | 'executors' | 'customers'>('all');
  const [mailCounts, setMailCounts] = useState<{
    unfinished: number;
    all: number;
    executors: number;
    customers: number;
  } | null>(null);
  const [mailErr, setMailErr] = useState<string | null>(null);
  const [mailBusy, setMailBusy] = useState<'unfinished' | 'registered' | null>(null);
  const [mailUnfinishedNote, setMailUnfinishedNote] = useState<string | null>(null);
  const [mailRegisteredNote, setMailRegisteredNote] = useState<string | null>(null);
  const [sourceSlug, setSourceSlug] = useState('');
  const [sourceCopied, setSourceCopied] = useState(false);

  const formatPromoCountdown = (endsAt: string | null | undefined, nowTs: number) => {
    if (!endsAt) return null;
    const end = new Date(endsAt).getTime();
    if (Number.isNaN(end)) return null;
    const ms = end - nowTs;
    if (ms <= 0) return 'закончилась';
    const totalSec = Math.floor(ms / 1000);
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (d > 0) return `${d}д ${h}ч ${m}м`;
    if (h > 0) return `${h}ч ${m}м ${String(s).padStart(2, '0')}с`;
    return `${m}м ${String(s).padStart(2, '0')}с`;
  };
  const [ready, setReady] = useState(false);
  const [monthlyRows, setMonthlyRows] = useState<MonthlyArchiveRow[]>([]);

  const [reportsTab, setReportsTab] = useState<'open' | 'closed'>('open');
  const [reports, setReports] = useState<AdminReportRow[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [disputeOrderId, setDisputeOrderId] = useState<number | null>(null);
  const [disputeData, setDisputeData] = useState<{
    order: Record<string, unknown>;
    messages: DisputeMessage[];
    customer: DisputeParty | null;
    executor: DisputeParty | null;
  } | null>(null);
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [disputeExtraBusy, setDisputeExtraBusy] = useState(false);
  const [moderationBusy, setModerationBusy] = useState<number | null>(null);
  const [verdictNote, setVerdictNote] = useState('');
  const [deleteOrderInput, setDeleteOrderInput] = useState('');
  const [deleteBanCustomer, setDeleteBanCustomer] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const ORDERS_PAGE_SIZE = 10;
  const [ordersList, setOrdersList] = useState<AdminOrderRow[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersStatusFilter, setOrdersStatusFilter] = useState<string>('all');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersErr, setOrdersErr] = useState<string | null>(null);
  const [orderRowDeleteBusy, setOrderRowDeleteBusy] = useState<number | null>(null);
  const ordersAbortRef = useRef<AbortController | null>(null);
  const ordersReqIdRef = useRef(0);

  const USERS_PAGE_SIZE = 10;
  const [usersList, setUsersList] = useState<AdminUserRow[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersRoleFilter, setUsersRoleFilter] = useState<string>('all');
  const [usersBannedFilter, setUsersBannedFilter] = useState<string>('all'); // all|1|0
  const [usersSearch, setUsersSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersErr, setUsersErr] = useState<string | null>(null);
  const usersAbortRef = useRef<AbortController | null>(null);
  const usersReqIdRef = useRef(0);
  const [usersPrivilegeBusy, setUsersPrivilegeBusy] = useState<number | null>(null);
  const [usersCsvBusy, setUsersCsvBusy] = useState(false);

  const [tab, setTab] = useState<AdminTab>('dashboard');
  const [adminTheme, setAdminTheme] = useState<AdminPaletteTheme>(() => readStoredAdminTheme());
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const ownerTgRef = useRef('');

  const [xpUsername, setXpUsername] = useState('');
  const [xpAmount, setXpAmount] = useState('');
  const [xpComment, setXpComment] = useState('');
  const [xpDirection, setXpDirection] = useState<'increase' | 'decrease'>('increase');
  const [xpBusy, setXpBusy] = useState(false);

  const fetchMonthlyArchive = useCallback(async (s: string) => {
    const trimmed = normalizeAdminSecret(s);
    if (trimmed.length < 16) return;
    try {
      const res = await adminFetch(apiUrl('/api/admin/stats/monthly?months=18'), {
        headers: { 'X-Admin-Secret': trimmed }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.months)) {
        setMonthlyRows(data.months);
      } else {
        setMonthlyRows([]);
      }
    } catch {
      setMonthlyRows([]);
    }
  }, []);

  const fetchWithdrawals = useCallback(
    async (s: string) => {
      const trimmed = normalizeAdminSecret(s);
      if (trimmed.length < 16) return;
      setWithdrawalsLoading(true);
      try {
        const res = await adminFetch(apiUrl('/api/admin/withdrawals?status=pending'), {
          headers: { 'X-Admin-Secret': trimmed }
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.requests)) {
          setWithdrawals(data.requests);
        } else {
          setWithdrawals([]);
        }
      } catch {
        setWithdrawals([]);
      } finally {
        setWithdrawalsLoading(false);
      }
    },
    []
  );

  const fetchOrdersForPage = useCallback(
    async (s: string, page: number, statusFilter: string) => {
      const trimmed = normalizeAdminSecret(s);
      if (trimmed.length < 16) return;
      ordersAbortRef.current?.abort();
      const ac = new AbortController();
      ordersAbortRef.current = ac;
      const myId = ++ordersReqIdRef.current;
      setOrdersLoading(true);
      setOrdersErr(null);
      try {
        const offset = page * ORDERS_PAGE_SIZE;
        const q = new URLSearchParams({
          limit: String(ORDERS_PAGE_SIZE),
          offset: String(offset)
        });
        if (statusFilter && statusFilter !== 'all') {
          q.set('status', statusFilter);
        }
        const res = await adminFetch(apiUrl(`/api/admin/orders?${q}`), {
          headers: { 'X-Admin-Secret': trimmed },
          signal: ac.signal
        });
        const text = await res.text();
        let data: {
          success?: boolean;
          orders?: unknown;
          total?: unknown;
          error?: string;
          detail?: string;
        } = {};
        try {
          data = text ? (JSON.parse(text) as typeof data) : {};
        } catch {
          if (myId !== ordersReqIdRef.current) return;
          setOrdersErr(
            'Ответ не JSON. Убедитесь, что на сервере обновлён бэкенд (есть GET /api/admin/orders) и proxy отдаёт API.'
          );
          setOrdersList([]);
          setOrdersTotal(0);
          return;
        }
        if (myId !== ordersReqIdRef.current) return;
        if (!res.ok) {
          setOrdersErr(String(data.detail || data.error || `HTTP ${res.status}`));
          setOrdersList([]);
          setOrdersTotal(0);
          return;
        }
        if (data.success && Array.isArray(data.orders)) {
          setOrdersList(data.orders as AdminOrderRow[]);
          const t = data.total;
          const totalNum =
            typeof t === 'number' && Number.isFinite(t) ? t : parseInt(String(t ?? '0'), 10);
          setOrdersTotal(Number.isFinite(totalNum) ? totalNum : 0);
          setOrdersPage(page);
        } else {
          setOrdersErr('Сервер вернул ответ без списка заказов');
          setOrdersList([]);
          setOrdersTotal(0);
        }
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        if (myId !== ordersReqIdRef.current) return;
        setOrdersErr(e instanceof Error ? e.message : 'Ошибка сети');
        setOrdersList([]);
        setOrdersTotal(0);
      } finally {
        if (myId === ordersReqIdRef.current) {
          setOrdersLoading(false);
        }
      }
    },
    [ORDERS_PAGE_SIZE]
  );

  const fetchUsersForPage = useCallback(
    async (s: string, page: number, roleFilter: string, bannedFilter: string, searchRaw: string) => {
      const trimmed = normalizeAdminSecret(s);
      if (trimmed.length < 16) return;
      usersAbortRef.current?.abort();
      const ac = new AbortController();
      usersAbortRef.current = ac;
      const myId = ++usersReqIdRef.current;
      setUsersLoading(true);
      setUsersErr(null);
      try {
        const offset = page * USERS_PAGE_SIZE;
        const q = new URLSearchParams({
          limit: String(USERS_PAGE_SIZE),
          offset: String(offset)
        });
        if (roleFilter && roleFilter !== 'all') q.set('role', roleFilter);
        if (bannedFilter && bannedFilter !== 'all') q.set('banned', bannedFilter);
        const search = String(searchRaw || '').trim();
        if (search) q.set('search', search);
        const res = await adminFetch(apiUrl(`/api/admin/users?${q}`), {
          headers: { 'X-Admin-Secret': trimmed },
          signal: ac.signal
        });
        const text = await res.text();
        let data: { success?: boolean; users?: unknown; total?: unknown; error?: string; detail?: string } = {};
        try {
          data = text ? (JSON.parse(text) as typeof data) : {};
        } catch {
          if (myId !== usersReqIdRef.current) return;
          setUsersErr('Ответ не JSON. Проверьте, что бэкенд обновлён (есть GET /api/admin/users).');
          setUsersList([]);
          setUsersTotal(0);
          return;
        }
        if (myId !== usersReqIdRef.current) return;
        if (!res.ok) {
          setUsersErr(String(data.detail || data.error || `HTTP ${res.status}`));
          setUsersList([]);
          setUsersTotal(0);
          return;
        }
        if (data.success && Array.isArray(data.users)) {
          setUsersList(data.users as AdminUserRow[]);
          const t = data.total;
          const totalNum =
            typeof t === 'number' && Number.isFinite(t) ? t : parseInt(String(t ?? '0'), 10);
          setUsersTotal(Number.isFinite(totalNum) ? totalNum : 0);
          setUsersPage(page);
        } else {
          setUsersErr('Сервер вернул ответ без списка пользователей');
          setUsersList([]);
          setUsersTotal(0);
        }
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        if (myId !== usersReqIdRef.current) return;
        setUsersErr(e instanceof Error ? e.message : 'Ошибка сети');
        setUsersList([]);
        setUsersTotal(0);
      } finally {
        if (myId === usersReqIdRef.current) setUsersLoading(false);
      }
    },
    [USERS_PAGE_SIZE]
  );

  const adminGrantPrivilege = useCallback(
    async (userId: number, kind: 'executor_top' | 'executor_vip' | 'customer_vip' | 'category_alerts') => {
      const trimmed = normalizeAdminSecret(secret);
      if (trimmed.length < 16) return;
      const daysRaw = window.prompt('Сколько дней добавить?', kind === 'category_alerts' ? '10' : '30');
      if (daysRaw == null) return;
      const days = parseInt(String(daysRaw).trim(), 10);
      if (!Number.isFinite(days) || days < 1) {
        alert('Некорректное число дней');
        return;
      }
      setUsersPrivilegeBusy(userId);
      try {
        const res = await adminFetch(apiUrl('/api/admin/users/grant-privilege'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Admin-Secret': trimmed },
          body: JSON.stringify({ username: String(userId), kind, days })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(String(data.error || data.detail || `HTTP ${res.status}`));
          return;
        }
        if (kind === 'category_alerts' && data.user_notified === false) {
          alert('Рассылка выдана, но уведомление исполнителю не отправилось.');
        }
        await fetchUsersForPage(trimmed, usersPage, usersRoleFilter, usersBannedFilter, usersSearch);
      } catch {
        alert('Ошибка сети');
      } finally {
        setUsersPrivilegeBusy(null);
      }
    },
    [secret, usersPage, usersRoleFilter, usersBannedFilter, usersSearch, fetchUsersForPage]
  );

  const downloadUsersCsv = useCallback(async () => {
    const trimmed = normalizeAdminSecret(secret);
    if (trimmed.length < 16) {
      alert('Сначала войдите с admin secret');
      return;
    }
    setUsersCsvBusy(true);
    try {
      const q = new URLSearchParams();
      if (usersRoleFilter && usersRoleFilter !== 'all') q.set('role', usersRoleFilter);
      if (usersBannedFilter === '1' || usersBannedFilter === '0') q.set('banned', usersBannedFilter);
      const s = usersSearch.trim();
      if (s) q.set('search', s);
      const res = await adminFetch(apiUrl(`/api/admin/users/export.csv?${q}`), {
        headers: { 'X-Admin-Secret': trimmed }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(String((data as { error?: string }).error || `HTTP ${res.status}`));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `freelancyx-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert('Ошибка сети при выгрузке CSV');
    } finally {
      setUsersCsvBusy(false);
    }
  }, [secret, usersRoleFilter, usersBannedFilter, usersSearch]);

  const adminRevokePrivilege = useCallback(
    async (userId: number, kind: 'executor_top' | 'executor_vip' | 'customer_vip' | 'category_alerts') => {
      const labels: Record<string, string> = {
        executor_top: 'ТОП исполнителя',
        executor_vip: 'VIP исполнителя',
        customer_vip: 'VIP заказчика',
        category_alerts: 'рассылку категорий'
      };
      if (!window.confirm(`Снять ${labels[kind] || kind} у пользователя #${userId}?`)) return;
      const trimmed = normalizeAdminSecret(secret);
      if (trimmed.length < 16) return;
      setUsersPrivilegeBusy(userId);
      try {
        const res = await adminFetch(apiUrl('/api/admin/users/revoke-privilege'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Admin-Secret': trimmed },
          body: JSON.stringify({ username: String(userId), kind })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(String(data.error || data.detail || `HTTP ${res.status}`));
          return;
        }
        await fetchUsersForPage(trimmed, usersPage, usersRoleFilter, usersBannedFilter, usersSearch);
      } catch {
        alert('Ошибка сети');
      } finally {
        setUsersPrivilegeBusy(null);
      }
    },
    [secret, usersPage, usersRoleFilter, usersBannedFilter, usersSearch, fetchUsersForPage]
  );

  const adminGrantUrgentSlots = useCallback(
    async (userId: number) => {
      const rawSlots = window.prompt('Сколько слотов срочных размещений добавить заказчику?', '3');
      if (rawSlots == null) return;
      const slots = parseInt(String(rawSlots).trim(), 10);
      if (!Number.isFinite(slots) || slots < 1 || slots > 99) {
        alert('Укажите число от 1 до 99');
        return;
      }
      const trimmed = normalizeAdminSecret(secret);
      if (trimmed.length < 16) return;
      setUsersPrivilegeBusy(userId);
      try {
        const res = await adminFetch(apiUrl('/api/admin/users/grant-urgent-slots'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Admin-Secret': trimmed },
          body: JSON.stringify({ username: String(userId), slots })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(String(data.error || data.detail || `HTTP ${res.status}`));
          return;
        }
        await fetchUsersForPage(trimmed, usersPage, usersRoleFilter, usersBannedFilter, usersSearch);
      } catch {
        alert('Ошибка сети');
      } finally {
        setUsersPrivilegeBusy(null);
      }
    },
    [secret, usersPage, usersRoleFilter, usersBannedFilter, usersSearch, fetchUsersForPage]
  );

  const adminGrantServiceChannelCredits = useCallback(
    async (userId: number) => {
      const rawSlots = window.prompt(
        'Сколько бустов публикации услуг в канал добавить исполнителю? (как пакеты 1/5/10)',
        '5'
      );
      if (rawSlots == null) return;
      const slots = parseInt(String(rawSlots).trim(), 10);
      if (!Number.isFinite(slots) || slots < 1 || slots > 99) {
        alert('Укажите число от 1 до 99');
        return;
      }
      const trimmed = normalizeAdminSecret(secret);
      if (trimmed.length < 16) return;
      setUsersPrivilegeBusy(userId);
      try {
        const res = await adminFetch(apiUrl('/api/admin/users/grant-urgent-slots'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Admin-Secret': trimmed },
          body: JSON.stringify({ username: String(userId), kind: 'service_channel', slots })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(String(data.error || data.detail || `HTTP ${res.status}`));
          return;
        }
        await fetchUsersForPage(trimmed, usersPage, usersRoleFilter, usersBannedFilter, usersSearch);
      } catch {
        alert('Ошибка сети');
      } finally {
        setUsersPrivilegeBusy(null);
      }
    },
    [secret, usersPage, usersRoleFilter, usersBannedFilter, usersSearch, fetchUsersForPage]
  );

  const fetchReports = useCallback(async (s: string, tab: 'open' | 'closed') => {
    const trimmed = normalizeAdminSecret(s);
    if (trimmed.length < 16) return;
    setReportsLoading(true);
    try {
      const res = await adminFetch(
        apiUrl(`/api/admin/reports?status=${encodeURIComponent(tab)}&limit=40`),
        { headers: { 'X-Admin-Secret': trimmed } }
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.reports)) {
        setReports(data.reports);
      } else {
        setReports([]);
      }
    } catch {
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const loadDispute = useCallback(
    async (s: string, orderIdRaw: string | number) => {
      const trimmed = normalizeAdminSecret(s);
      if (trimmed.length < 16) return;
      const orderId = parseAdminOrderId(String(orderIdRaw));
      if (orderId == null) return;
      setDisputeOrderId(orderId);
      setDisputeLoading(true);
      setDisputeData(null);
      try {
        const res = await adminFetch(apiUrl(`/api/admin/orders/${orderId}/dispute`), {
          headers: { 'X-Admin-Secret': trimmed }
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.detail || data.error || `HTTP ${res.status}`);
        }
        if (data.success) {
          setDisputeData({
            order: data.order,
            messages: Array.isArray(data.messages) ? data.messages : [],
            customer: data.customer ?? null,
            executor: data.executor ?? null
          });
        }
      } catch (e) {
        setDisputeData(null);
        setErr(e instanceof Error ? e.message : 'Ошибка загрузки спора');
      } finally {
        setDisputeLoading(false);
      }
    },
    []
  );

  const fetchPromoSlides = useCallback(async (s: string) => {
    const trimmed = normalizeAdminSecret(s);
    if (trimmed.length < 16) return;
    setPromoLoading(true);
    setPromoErr(null);
    try {
      const res = await adminFetch(apiUrl('/api/admin/home-promo'), {
        headers: { 'X-Admin-Secret': trimmed }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setPromoSlides(Array.isArray(data.slides) ? data.slides : []);
      if (data.image_spec) setPromoImageSpec(data.image_spec);
    } catch (e) {
      setPromoErr(e instanceof Error ? e.message : 'Ошибка загрузки промо');
      setPromoSlides([]);
    } finally {
      setPromoLoading(false);
    }
  }, []);

  const fetchAcquisition = useCallback(async (s: string) => {
    const trimmed = normalizeAdminSecret(s);
    if (trimmed.length < 16) return;
    setAcquisitionLoading(true);
    setAcquisitionErr(null);
    try {
      const res = await adminFetch(apiUrl('/api/admin/acquisition'), {
        headers: { 'X-Admin-Secret': trimmed }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const bot = String(data.bot_username || 'freelancyxbot').replace(/^@/, '');
      setAcquisitionBot(bot || 'freelancyxbot');
      setAcquisitionRows(Array.isArray(data.sources) ? data.sources : []);
    } catch (e) {
      setAcquisitionErr(e instanceof Error ? e.message : 'Ошибка загрузки посевов');
      setAcquisitionRows([]);
    } finally {
      setAcquisitionLoading(false);
    }
  }, []);

  const fetchMailCounts = useCallback(async (s: string) => {
    const trimmed = normalizeAdminSecret(s);
    if (trimmed.length < 16) return;
    setMailErr(null);
    try {
      const res = await adminFetch(apiUrl('/api/admin/broadcast/preview'), {
        headers: { 'X-Admin-Secret': trimmed }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const c = data.counts || {};
      setMailCounts({
        unfinished: Number(c.unfinished) || 0,
        all: Number(c.all) || 0,
        executors: Number(c.executors) || 0,
        customers: Number(c.customers) || 0
      });
    } catch (e) {
      setMailErr(e instanceof Error ? e.message : 'Не удалось посчитать получателей');
    }
  }, []);

  const sendMail = async (audience: 'unfinished' | 'all' | 'executors' | 'customers', text: string) => {
    const trimmed = normalizeAdminSecret(secret);
    const body = text.trim();
    if (!body) {
      setMailErr('Напишите текст сообщения');
      return;
    }
    const count =
      audience === 'unfinished'
        ? mailCounts?.unfinished
        : audience === 'executors'
          ? mailCounts?.executors
          : audience === 'customers'
            ? mailCounts?.customers
            : mailCounts?.all;
    const who =
      audience === 'unfinished'
        ? 'тем, кто не закончил регистрацию'
        : audience === 'executors'
          ? 'исполнителям'
          : audience === 'customers'
            ? 'заказчикам'
            : 'заказчикам и исполнителям';
    if (!window.confirm(`Отправить сообщение ${who}${count != null ? ` (${count})` : ''}?`)) return;
    const busyKey = audience === 'unfinished' ? 'unfinished' : 'registered';
    setMailBusy(busyKey);
    setMailErr(null);
    if (audience === 'unfinished') setMailUnfinishedNote(null);
    else setMailRegisteredNote(null);
    try {
      const res = await adminFetch(apiUrl('/api/admin/broadcast'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Secret': trimmed },
        body: JSON.stringify({ audience, text: body })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const note = `Отправлено: ${Number(data.sent) || 0} из ${Number(data.recipients) || 0}. Не дошло: ${Number(data.not_delivered) || 0}. Ошибок: ${Number(data.failed) || 0}.`;
      if (audience === 'unfinished') setMailUnfinishedNote(note);
      else setMailRegisteredNote(note);
      void fetchMailCounts(trimmed);
    } catch (e) {
      setMailErr(e instanceof Error ? e.message : 'Не удалось отправить');
    } finally {
      setMailBusy(null);
    }
  };

  const buildPromoActionsFromForm = () => {
    const actions: Array<{ label: string; cta: { type: string; url?: string } }> = [];
    if (promoForm.btn1_label.trim() && promoForm.btn1_type !== 'none') {
      const cta: { type: string; url?: string } = { type: promoForm.btn1_type };
      if (promoForm.btn1_type === 'external') cta.url = promoForm.btn1_url.trim();
      actions.push({ label: promoForm.btn1_label.trim(), cta });
    }
    if (promoForm.btn2_label.trim() && promoForm.btn2_type !== 'none') {
      const cta: { type: string; url?: string } = { type: promoForm.btn2_type };
      if (promoForm.btn2_type === 'external') cta.url = promoForm.btn2_url.trim();
      actions.push({ label: promoForm.btn2_label.trim(), cta });
    }
    return actions;
  };

  const fillPromoFormFromSlide = (s: PromoSlideAdmin) => {
    const a0 = s.actions?.[0];
    const a1 = s.actions?.[1];
    setPromoEditId(s.id);
    setPromoForm({
      title: s.title || '',
      subtitle: s.subtitle || '',
      tone: s.tone || 'blue',
      audience: s.audience || 'all',
      image_url: s.image_url || '',
      show_telegram_mark: Boolean(s.show_telegram_mark),
      sort_order: String(s.sort_order ?? 0),
      is_active: Boolean(s.is_active),
      duration_days:
        s.duration_days == null || s.duration_days === undefined ? '0' : String(s.duration_days),
      btn1_label: a0?.label || 'Перейти',
      btn1_type: a0?.cta?.type || 'external',
      btn1_url: a0?.cta?.url || '',
      btn2_label: a1?.label || '',
      btn2_type: a1?.cta?.type || 'none',
      btn2_url: a1?.cta?.url || ''
    });
  };

  const uploadPromoImageFile = async (file: File) => {
    const trimmed = normalizeAdminSecret(secret);
    if (trimmed.length < 16) return;
    setPromoBusy(true);
    setPromoErr(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await adminFetch(apiUrl('/api/admin/home-promo/upload'), {
        method: 'POST',
        headers: { 'X-Admin-Secret': trimmed },
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.image_url) {
        setPromoForm((f) => ({ ...f, image_url: String(data.image_url) }));
      }
      if (data.image_spec) setPromoImageSpec(data.image_spec);
    } catch (e) {
      setPromoErr(e instanceof Error ? e.message : 'Ошибка загрузки картинки');
    } finally {
      setPromoBusy(false);
    }
  };

  const savePromoSlide = async () => {
    const trimmed = normalizeAdminSecret(secret);
    if (trimmed.length < 16) return;
    if (!promoForm.title.trim() && !promoForm.image_url.trim()) {
      setPromoErr('Нужен заголовок или картинка');
      return;
    }
    setPromoBusy(true);
    setPromoErr(null);
    try {
      const durRaw = promoForm.duration_days;
      const duration_days =
        durRaw === '0' || durRaw === '' || durRaw === 'unlimited' ? null : parseInt(durRaw, 10) || null;
      const body = {
        title: promoForm.title.trim(),
        subtitle: promoForm.subtitle.trim() || null,
        tone: promoForm.tone,
        audience: promoForm.audience,
        image_url: promoForm.image_url.trim() || null,
        show_telegram_mark: promoForm.show_telegram_mark,
        sort_order: parseInt(promoForm.sort_order, 10) || 0,
        is_active: promoForm.is_active,
        duration_days,
        actions: buildPromoActionsFromForm()
      };
      const url = promoEditId
        ? apiUrl(`/api/admin/home-promo/${promoEditId}`)
        : apiUrl('/api/admin/home-promo');
      const res = await adminFetch(url, {
        method: promoEditId ? 'PUT' : 'POST',
        headers: {
          'X-Admin-Secret': trimmed,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setPromoEditId(null);
      setPromoForm(emptyPromoForm());
      await fetchPromoSlides(trimmed);
    } catch (e) {
      setPromoErr(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setPromoBusy(false);
    }
  };

  const togglePromoActive = async (id: number, next: boolean) => {
    const trimmed = normalizeAdminSecret(secret);
    if (trimmed.length < 16) return;
    setPromoBusy(true);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/home-promo/${id}/active`), {
        method: 'POST',
        headers: {
          'X-Admin-Secret': trimmed,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: next })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await fetchPromoSlides(trimmed);
    } catch (e) {
      setPromoErr(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setPromoBusy(false);
    }
  };

  const deletePromoSlide = async (id: number) => {
    if (!window.confirm(`Удалить слайд #${id}?`)) return;
    const trimmed = normalizeAdminSecret(secret);
    if (trimmed.length < 16) return;
    setPromoBusy(true);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/home-promo/${id}`), {
        method: 'DELETE',
        headers: { 'X-Admin-Secret': trimmed }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (promoEditId === id) {
        setPromoEditId(null);
        setPromoForm(emptyPromoForm());
      }
      await fetchPromoSlides(trimmed);
    } catch (e) {
      setPromoErr(e instanceof Error ? e.message : 'Ошибка удаления');
    } finally {
      setPromoBusy(false);
    }
  };

  const seedPromoDefaults = async (force: boolean) => {
    const trimmed = normalizeAdminSecret(secret);
    if (trimmed.length < 16) return;
    if (force && !window.confirm('Добавить дефолтные слайды поверх существующих?')) return;
    setPromoBusy(true);
    setPromoErr(null);
    try {
      const res = await adminFetch(apiUrl('/api/admin/home-promo/seed'), {
        method: 'POST',
        headers: {
          'X-Admin-Secret': trimmed,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ force })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (!data.seeded && data.reason === 'already_has_slides') {
        setPromoErr('Уже есть слайды. Нажмите «Добавить дефолты» с подтверждением, чтобы дописать.');
      }
      await fetchPromoSlides(trimmed);
    } catch (e) {
      setPromoErr(e instanceof Error ? e.message : 'Ошибка seed');
    } finally {
      setPromoBusy(false);
    }
  };

  const fetchStats = useCallback(
    async (s: string) => {
      const trimmed = normalizeAdminSecret(s);
      if (trimmed.length < 16) {
        setErr('Секрет слишком короткий (на сервере нужен ≥ 16 символов)');
        return;
      }
      if (!ownerTgRef.current || !ownerBearer) {
        setErr('Откройте админку из Telegram своим аккаунтом');
        return;
      }
      setLoading(true);
      setErr(null);
      try {
        const url = apiUrl('/api/admin/stats');
        const res = await adminFetch(url, {
          headers: { 'X-Admin-Secret': trimmed }
        });
        const text = await res.text();
        let data: Stats = {};
        try {
          data = text ? (JSON.parse(text) as Stats) : {};
        } catch {
          throw new Error(
            'Ответ сервера не JSON. Проверьте, что фронт ходит на тот же домен, что API (пересоберите с REACT_APP_API_URL).'
          );
        }
        if (!res.ok) {
          if (res.status === 403) {
            forgetAdminSecret(ownerTgRef.current);
            setSecret('');
          }
          throw new Error(data.detail || data.error || `HTTP ${res.status}`);
        }
        setStats(data);
        rememberAdminSecret(ownerTgRef.current, trimmed);
        setSecret(trimmed);
        void fetchWithdrawals(trimmed);
        void fetchMonthlyArchive(trimmed);
        void fetchOrdersForPage(trimmed, 0, ordersStatusFilter);
        void fetchUsersForPage(trimmed, 0, 'all', 'all', '');
        void fetchPromoSlides(trimmed);
        void fetchAcquisition(trimmed);
      } catch (e) {
        setStats(null);
        let msg = e instanceof Error ? e.message : 'Ошибка запроса';
        if (msg.includes('did not match the expected pattern')) {
          msg =
            'Сбой адреса API в Telegram WebView. На сервере выполните: npm run build в frontend с .env.production (REACT_APP_API_URL=ваш https-домен) и выложите build заново.';
        }
        setErr(msg);
      } finally {
        setLoading(false);
      }
    },
    [fetchWithdrawals, fetchMonthlyArchive, fetchOrdersForPage, fetchUsersForPage, ordersStatusFilter, fetchAcquisition]
  );

  useEffect(() => {
    if (!secret.trim() || secret.trim().length < 16) return;
    void fetchReports(secret, reportsTab);
  }, [secret, reportsTab, fetchReports]);

  useEffect(() => {
    if (tab !== 'promo') return;
    setPromoNowTs(Date.now());
    const t = window.setInterval(() => setPromoNowTs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [tab]);

  function orderFromAdminHash(): string | null {
    const h = window.location.hash.replace(/^#/, '');
    if (!h.startsWith('admin')) return null;
    const q = h.indexOf('?');
    if (q === -1) return null;
    return new URLSearchParams(h.slice(q + 1)).get('order');
  }

  useEffect(() => {
    const oid = orderFromAdminHash();
    if (oid && secret.trim().length >= 16) {
      void loadDispute(secret, oid);
    }
  }, [secret, loadDispute]);

  useEffect(() => {
    const onHash = () => {
      const oid = orderFromAdminHash();
      if (oid && secret.trim().length >= 16) void loadDispute(secret, oid);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [secret, loadDispute]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const token = await ensureOwnerToken();
      if (cancel) return;
      ownerBearer = token;
      const tgId = telegramIdOf(token);
      ownerTgRef.current = tgId;
      let saved = '';
      if (tgId) {
        try {
          saved = localStorage.getItem(secretStorageKey(tgId)) || '';
        } catch {
          saved = '';
        }
      }
      if (!saved) {
        try {
          saved = sessionStorage.getItem(STORAGE_KEY) || '';
        } catch {
          saved = '';
        }
      }
      const s = saved ? normalizeAdminSecret(saved) : '';
      if (s.length >= 16 && tgId) {
        setSecret(s);
        void fetchStats(s);
        void fetchWithdrawals(s);
        void fetchMonthlyArchive(s);
      }
      setReady(true);
    })();
    return () => {
      cancel = true;
    };
  }, [fetchStats, fetchWithdrawals, fetchMonthlyArchive]);

  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-p', adminTheme);
    } catch {
      // ignore
    }
  }, [adminTheme]);

  useEffect(() => {
    if (!themeMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (themeMenuRef.current && !themeMenuRef.current.contains(e.target as Node)) {
        setThemeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [themeMenuOpen]);

  const selectAdminTheme = (id: AdminPaletteTheme) => {
    setAdminTheme(id);
    try {
      window.localStorage.setItem(ADMIN_THEME_KEY, id);
    } catch {
      // ignore
    }
    setThemeMenuOpen(false);
  };

  const login = () => void fetchStats(input);

  const completeWithdrawal = async (id: number) => {
    if (!secret) return;
    if (
      !window.confirm(
        'Вы уже отправили USDT пользователю через Crypto Pay / @CryptoBot?\n\nНажмите OK, чтобы закрыть заявку.'
      )
    )
      return;
    setActionBusy(id);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/withdrawals/${id}/complete`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({ admin_note: 'Выполнено через админку' })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Ошибка');
        return;
      }
      alert('Заявка закрыта.');
      await fetchWithdrawals(secret);
      void fetchStats(secret);
      void fetchMonthlyArchive(secret);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setActionBusy(null);
    }
  };

  const rejectWithdrawal = async (id: number) => {
    if (!secret) return;
    const note = window.prompt('Причина отклонения (опционально, Enter — без причины):');
    if (note === null) return;
    setActionBusy(id);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/withdrawals/${id}/reject`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({ admin_note: note || undefined })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Ошибка');
        return;
      }
      alert('Заявка отклонена. USDT возвращены на баланс пользователя.');
      await fetchWithdrawals(secret);
      void fetchStats(secret);
      void fetchMonthlyArchive(secret);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setActionBusy(null);
    }
  };

  const logout = () => {
    forgetAdminSecret(ownerTgRef.current);
    setSecret('');
    setInput('');
    setStats(null);
    setWithdrawals([]);
    setReports([]);
    setOrdersList([]);
    setOrdersTotal(0);
    setOrdersPage(0);
    setOrdersErr(null);
    setUsersList([]);
    setUsersTotal(0);
    setUsersPage(0);
    setUsersErr(null);
    setDisputeData(null);
    setDisputeOrderId(null);
    setErr(null);
  };

  const postDisputeResolve = async (action: 'refund_customer' | 'pay_executor') => {
    if (!secret || disputeOrderId == null) return;
    const st = String((disputeData?.order as { status?: string } | undefined)?.status || '');
    const msg =
      action === 'pay_executor'
        ? st === 'awaiting_payment'
          ? 'Списать USDT с баланса заказчика и выплатить исполнителю?'
          : 'Выплатить исполнителю из эскроу и закрыть заказ?'
        : 'Отменить заказ и вернуть средства заказчику там, где они заморожены (или закрыть промо без оплаты)?';
    if (!window.confirm(msg)) return;
    setModerationBusy(disputeOrderId);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/orders/${disputeOrderId}/dispute-resolve`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({
          action,
          admin_note: verdictNote.trim() || undefined
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || data.detail || 'Ошибка');
        return;
      }
      alert(data.message || 'Готово');
      void loadDispute(secret, disputeOrderId);
      void fetchStats(secret);
      void fetchReports(secret, reportsTab);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setModerationBusy(null);
    }
  };

  const closeReportById = async (reportId: number) => {
    if (!secret) return;
    if (!window.confirm(`Закрыть жалобу #${reportId}?`)) return;
    setModerationBusy(reportId);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/reports/${reportId}/close`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({ admin_note: verdictNote.trim() || undefined })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Ошибка');
        return;
      }
      void fetchReports(secret, reportsTab);
      void fetchStats(secret);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setModerationBusy(null);
    }
  };

  const applyTrustDelta = async (userId: number, delta: number) => {
    if (!secret) return;
    setModerationBusy(userId);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/users/${userId}/trust-delta`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({ delta })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Ошибка');
        return;
      }
      if (disputeOrderId) void loadDispute(secret, disputeOrderId);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setModerationBusy(null);
    }
  };

  const banUserById = async (userId: number) => {
    if (!secret) return;
    const reason = window.prompt('Причина блокировки:', verdictNote.trim() || 'Решение по жалобе');
    if (reason === null) return;
    setModerationBusy(userId);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/users/${userId}/ban`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({ reason: reason || 'Блокировка администратором' })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Ошибка');
        return;
      }
      if (disputeOrderId) void loadDispute(secret, disputeOrderId);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setModerationBusy(null);
    }
  };

  const postRemoveExecutor = async () => {
    if (!secret || disputeOrderId == null) return;
    if (!window.confirm('Снять исполнителя и вернуть заказ в ленту?')) return;
    setDisputeExtraBusy(true);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/orders/${disputeOrderId}/remove-executor`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || data.detail || 'Ошибка');
        return;
      }
      alert(data.message || 'Готово');
      void loadDispute(secret, disputeOrderId);
      void fetchStats(secret);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setDisputeExtraBusy(false);
    }
  };

  const postParticipantXpPenalty = async (role: 'customer' | 'executor') => {
    if (!secret || disputeOrderId == null) return;
    const who = role === 'customer' ? 'заказчика' : 'исполнителя';
    if (!window.confirm(`Списать 20 XP с ${who}?`)) return;
    setDisputeExtraBusy(true);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/orders/${disputeOrderId}/participant-xp-penalty`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({
          role,
          amount: 20,
          comment: verdictNote.trim() || undefined
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || data.detail || 'Ошибка');
        return;
      }
      alert(`У пользователя теперь ${data.new_xp} XP`);
      void loadDispute(secret, disputeOrderId);
      void fetchStats(secret);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setDisputeExtraBusy(false);
    }
  };

  const deleteOrderFromTable = async (row: AdminOrderRow) => {
    if (!secret) return;
    if (!['open', 'cancelled'].includes(row.status)) {
      alert('Удалить из базы можно только заказы в статусах open или cancelled (остальное — через блок «Спор»).');
      return;
    }
    if (
      !window.confirm(
        `Удалить ${orderRefLabel(row.id)}? Для «open» вернётся эскроу или промо-квота заказчику.`
      )
    ) {
      return;
    }
    setOrderRowDeleteBusy(row.id);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/orders/${row.id}/delete`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({
          reason: 'Удаление из таблицы админки',
          ban_customer: false
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || data.error || 'Ошибка');
        return;
      }
      void fetchOrdersForPage(secret, ordersPage, ordersStatusFilter);
      void fetchStats(secret);
      if (disputeOrderId === row.id) {
        setDisputeData(null);
        setDisputeOrderId(null);
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setOrderRowDeleteBusy(null);
    }
  };

  const postDeleteOrder = async () => {
    if (!secret) return;
    const orderId = parseAdminOrderId(deleteOrderInput);
    if (orderId == null) {
      alert('Укажите номер заказа: число или FRL-0029');
      return;
    }
    if (
      !window.confirm(
        'Удалить заказ из базы? Для статуса «open» вернутся эскроу или промо-квота заказчику. Для других статусов сначала отмените заказ в блоке «Спор».'
      )
    ) {
      return;
    }
    let banReasonPayload: string | undefined;
    if (deleteBanCustomer) {
      const br = window.prompt('Причина блокировки заказчика:', verdictNote.trim() || 'Нарушение правил платформы');
      if (br === null) return;
      banReasonPayload = br || 'Нарушение правил платформы';
    }
    setDeleteBusy(true);
    try {
      const res = await adminFetch(apiUrl(`/api/admin/orders/${orderId}/delete`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({
          reason: verdictNote.trim() || undefined,
          ban_customer: deleteBanCustomer,
          ...(banReasonPayload != null ? { ban_reason: banReasonPayload } : {})
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.detail || data.error || 'Ошибка');
        return;
      }
      alert(data.message || 'Удалено');
      setDeleteOrderInput('');
      setDeleteBanCustomer(false);
      if (disputeOrderId === orderId) {
        setDisputeData(null);
        setDisputeOrderId(null);
      }
      void fetchStats(secret);
      void fetchOrdersForPage(secret, ordersPage, ordersStatusFilter);
      void fetchReports(secret, reportsTab);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setDeleteBusy(false);
    }
  };

  const postXpAdjust = async () => {
    if (!secret) return;
    const rawUser = xpUsername.trim();
    const amount = parseInt(String(xpAmount).trim(), 10);
    if (!rawUser) {
      alert('Укажите @username или числовой id пользователя');
      return;
    }
    if (!Number.isFinite(amount) || amount < 1) {
      alert('Сумма XP: целое число ≥ 1');
      return;
    }
    setXpBusy(true);
    try {
      const res = await adminFetch(apiUrl('/api/admin/users/xp-adjust'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Secret': secret
        },
        body: JSON.stringify({
          username: rawUser,
          direction: xpDirection,
          amount,
          ...(xpComment.trim() ? { comment: xpComment.trim() } : {})
        })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(String(data.detail || data.error || 'Ошибка'));
        return;
      }
      const notified = data.user_notified !== false;
      alert(
        `Готово. Было ${data.old_xp}, стало ${data.new_xp} (изменение ${data.delta_applied > 0 ? '+' : ''}${data.delta_applied}). ${
          notified
            ? 'Уведомление в бот отправлено.'
            : 'Уведомление в бот НЕ отправлено — проверьте BOT_INTERNAL_SECRET и процесс бота.'
        }`
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setXpBusy(false);
    }
  };

  const panelStyle: React.CSSProperties = {
    background: 'var(--fc-card)',
    borderRadius: '14px',
    padding: '16px',
    marginBottom: '12px',
    border: '1px solid var(--fc-border)',
    boxShadow: 'var(--fc-shadow)',
    textAlign: 'left' as const
  };

  const promoBtn: React.CSSProperties = {
    fontSize: '12px',
    padding: '6px 10px',
    borderRadius: '8px',
    border: '1px solid var(--fc-border)',
    background: 'var(--fc-card-hover)',
    color: 'var(--fc-text-muted)',
    cursor: 'pointer'
  };

  if (!ready) {
    return (
      <div className="App">
        <header className="App-header">
          <p>…</p>
        </header>
      </div>
    );
  }

  if (!secret.trim()) {
    return (
      <div className="App">
        <header className="App-header" style={{ maxWidth: '420px', margin: '0 auto', padding: '20px' }}>
          <h1 style={{ fontSize: '22px' }}>FreelanCYX — админка</h1>
          <p style={{ fontSize: '13px', color: 'var(--fc-text-muted)' }}>
            Один раз вставьте <code style={{ color: 'var(--fc-accent)' }}>ADMIN_SECRET</code> из{' '}
            <code style={{ color: 'var(--fc-accent)' }}>backend/.env</code>. Этот телефон запомнит его для вашего
            Telegram-аккаунта. С другого аккаунта админка не откроется.
          </p>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              login();
            }}
            style={{ width: '100%', maxWidth: '360px' }}
          >
            <input
              type="text"
              inputMode="text"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Секрет (как в ADMIN_SECRET)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid var(--fc-input-border)',
                background: 'var(--fc-input-bg)',
                color: 'var(--fc-input-text)',
                marginTop: '12px',
                boxSizing: 'border-box'
              }}
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: '16px',
                padding: '12px 24px',
                borderRadius: '10px',
                border: 'none',
                background: 'var(--fc-accent)',
                color: 'var(--fc-on-accent)',
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%'
              }}
            >
              {loading ? '…' : 'Показать статистику'}
            </button>
          </form>
          {err && <p style={{ color: 'var(--fc-danger)', fontSize: '13px', marginTop: '12px' }}>{err}</p>}
          <p style={{ marginTop: '24px', fontSize: '12px', color: 'var(--fc-text-muted)' }}>
            URL с хэшем: <code>#admin</code>
          </p>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header" style={{ maxWidth: '480px', margin: '0 auto', padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={{ fontSize: '20px', margin: 0 }}>📊 Админка FreelanCYX</h1>
          <button
            type="button"
            onClick={logout}
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              borderRadius: '8px',
              border: '1px solid var(--fc-border)',
              background: 'transparent',
              color: 'var(--fc-text-muted)',
              cursor: 'pointer'
            }}
          >
            Выйти
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginBottom: '12px',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {(
              [
                { id: 'dashboard', label: 'Сводка' },
                { id: 'orders', label: 'Заказы' },
                { id: 'users', label: 'Пользователи' },
                { id: 'promo', label: 'Реклама' },
                { id: 'sources', label: 'Посевы' },
                { id: 'mail', label: 'Рассылка' }
              ] as Array<{ id: AdminTab; label: string }>
            ).map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTab(t.id);
                  if (t.id === 'promo' && secret) void fetchPromoSlides(secret);
                  if (t.id === 'sources' && secret) void fetchAcquisition(secret);
                  if (t.id === 'mail' && secret) void fetchMailCounts(secret);
                }}
                style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  borderRadius: '999px',
                  border: tab === t.id ? '1px solid rgba(var(--fc-accent-rgb), 0.5)' : '1px solid var(--fc-border)',
                  background: tab === t.id ? 'rgba(var(--fc-accent-rgb), 0.16)' : 'var(--fc-card)',
                  color: tab === t.id ? 'var(--fc-accent)' : 'var(--fc-text-muted)',
                  cursor: 'pointer'
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div ref={themeMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setThemeMenuOpen((v) => !v)}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                borderRadius: '999px',
                border: themeMenuOpen ? '1px solid rgba(var(--fc-accent-rgb), 0.5)' : '1px solid var(--fc-border)',
                background: themeMenuOpen ? 'rgba(var(--fc-accent-rgb), 0.16)' : 'var(--fc-card)',
                color: themeMenuOpen ? 'var(--fc-accent)' : 'var(--fc-text-muted)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              🎨 Тема
            </button>
            {themeMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  minWidth: '150px',
                  background: 'var(--fc-card)',
                  border: '1px solid var(--fc-border)',
                  borderRadius: '12px',
                  boxShadow: 'var(--fc-shadow-float)',
                  padding: '6px',
                  zIndex: 30
                }}
              >
                {ADMIN_THEMES.map((th) => (
                  <button
                    key={th.id}
                    type="button"
                    onClick={() => selectAdminTheme(th.id)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      borderRadius: '8px',
                      border: 'none',
                      background: adminTheme === th.id ? 'rgba(var(--fc-accent-rgb), 0.16)' : 'transparent',
                      color: adminTheme === th.id ? 'var(--fc-accent)' : 'var(--fc-text)',
                      fontSize: '13px',
                      fontWeight: adminTheme === th.id ? 600 : 400,
                      cursor: 'pointer'
                    }}
                  >
                    {th.label}
                    {adminTheme === th.id ? ' ✓' : ''}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {loading && <p style={{ color: 'var(--fc-text-muted)' }}>Загрузка…</p>}
        {err && !loading && (
          <p style={{ color: 'var(--fc-danger)', fontSize: '14px' }}>
            {err}{' '}
            <button
              type="button"
              onClick={() => void fetchStats(secret)}
              style={{ color: 'var(--fc-accent)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Повторить
            </button>
          </p>
        )}

        {tab === 'dashboard' && stats?.generated_at && (
          <p style={{ fontSize: '11px', color: 'var(--fc-text-muted)', marginBottom: '12px' }}>
            Обновлено: {new Date(stats.generated_at).toLocaleString('ru-RU')}
          </p>
        )}

        {tab === 'dashboard' && stats?.users && (
          <div style={panelStyle}>
            <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-accent)' }}>Пользователи</h2>
            <StatRow label="Всего (без служебной кассы)" value={stats.users.total_users ?? 0} />
            <StatRow label="Новых сегодня" value={stats.users.new_users_today ?? 0} />
            <StatRow label="Заказчики" value={stats.users.customers ?? 0} />
            <StatRow label="Исполнители" value={stats.users.executors ?? 0} />
            <StatRow label="Роль не выбрана" value={stats.users.pending_role ?? 0} />
            <StatRow label="Заблокированы" value={stats.users.banned_users ?? 0} />
            <StatRow label="VIP (активная подписка по дате)" value={stats.users.vip_with_active_subscription ?? 0} />
            <StatRow label="VIP (флаг is_vip)" value={stats.users.vip_with_flag_total ?? 0} />
          </div>
        )}

        {tab === 'dashboard' && stats?.orders && (
          <div style={panelStyle}>
            <React.Fragment>
              <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-accent)' }}>Заказы</h2>
              <StatRow label="Всего заданий" value={Number(stats.orders.orders_total) || 0} />
              <StatRow
                label="Активные (открыто / в работе / выполнено / ожидает оплаты)"
                value={Number(stats.orders.orders_active) || 0}
              />
              <StatRow label="Архив (завершено / отменено)" value={Number(stats.orders.orders_archived) || 0} />
              <StatRow label="Откликов всего" value={Number(stats.orders.responses_total) || 0} />
              {stats.orders.by_status ? (
                <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--fc-text-muted)' }}>
                  По статусам:{' '}
                  {Object.entries(stats.orders.by_status as Record<string, number>)
                    .map(([k, v]) => `${orderStatusLabel(k)}: ${v}`)
                    .join(' · ')}
                </div>
              ) : null}
            </React.Fragment>
          </div>
        )}

        {tab === 'orders' ? (
          <div style={panelStyle}>
          <h2 style={{ margin: '0 0 8px', fontSize: '16px', color: 'var(--fc-accent)' }}>Все заказы</h2>
          <p style={{ fontSize: '12px', color: 'var(--fc-text-muted)', margin: '0 0 10px', lineHeight: 1.45 }}>
            Открытые задания без откликов старше месяца удаляются автоматически (раз в ~5 мин с возвратом
            эскроу или промо-квоты).
          </p>
          {ordersErr ? (
            <p
              style={{
                fontSize: '13px',
                color: 'var(--fc-danger)',
                margin: '0 0 10px',
                lineHeight: 1.45
              }}
            >
              {ordersErr}
            </p>
          ) : null}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
            <label style={{ fontSize: '13px', color: 'var(--fc-text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              Статус
              <select
                value={ordersStatusFilter}
                onChange={(e) => {
                  const v = e.target.value;
                  setOrdersStatusFilter(v);
                  void fetchOrdersForPage(secret, 0, v);
                }}
                style={{
                  padding: '6px 8px',
                  borderRadius: '8px',
                  border: '1px solid var(--fc-input-border)',
                  background: 'var(--fc-input-bg)',
                  color: 'var(--fc-input-text)',
                }}
              >
                <option value="all">Все статусы</option>
                <option value="open">{orderStatusLabel('open')}</option>
                <option value="in_progress">{orderStatusLabel('in_progress')}</option>
                <option value="completed">{orderStatusLabel('completed')}</option>
                <option value="awaiting_payment">{orderStatusLabel('awaiting_payment')}</option>
                <option value="finished">{orderStatusLabel('finished')}</option>
                <option value="cancelled">{orderStatusLabel('cancelled')}</option>
              </select>
            </label>
            <button
              type="button"
              disabled={ordersLoading}
              onClick={() => void fetchOrdersForPage(secret, ordersPage, ordersStatusFilter)}
              style={{
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid var(--fc-border)',
                background: 'transparent',
                color: 'var(--fc-text-muted)',
                cursor: ordersLoading ? 'wait' : 'pointer',
                fontSize: '12px'
              }}
            >
              Обновить
            </button>
            <span style={{ fontSize: '12px', color: 'var(--fc-text-muted)' }}>
              {ordersTotal} всего · стр. {ordersPage + 1} / {Math.max(1, Math.ceil(ordersTotal / ORDERS_PAGE_SIZE))}
            </span>
            <button
              type="button"
              disabled={ordersLoading || ordersPage <= 0}
              onClick={() => void fetchOrdersForPage(secret, ordersPage - 1, ordersStatusFilter)}
              style={{
                padding: '6px 10px',
                borderRadius: '8px',
                border: '1px solid var(--fc-border)',
                background: 'transparent',
                color: 'var(--fc-text-muted)',
                cursor: ordersPage <= 0 ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              ←
            </button>
            <button
              type="button"
              disabled={ordersLoading || (ordersPage + 1) * ORDERS_PAGE_SIZE >= ordersTotal}
              onClick={() => void fetchOrdersForPage(secret, ordersPage + 1, ordersStatusFilter)}
              style={{
                padding: '6px 10px',
                borderRadius: '8px',
                border: '1px solid var(--fc-border)',
                background: 'transparent',
                color: 'var(--fc-text-muted)',
                cursor: (ordersPage + 1) * ORDERS_PAGE_SIZE >= ordersTotal ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              →
            </button>
          </div>
          {ordersLoading ? (
            <p style={{ color: 'var(--fc-text-muted)', fontSize: '13px' }}>Загрузка таблицы…</p>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table
                style={{
                  width: '100%',
                  minWidth: '720px',
                  borderCollapse: 'collapse',
                  fontSize: '12px',
                  color: 'var(--fc-text)'
                }}
              >
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <th style={{ padding: '8px 6px' }}>№</th>
                    <th style={{ padding: '8px 6px' }}>Статус</th>
                    <th style={{ padding: '8px 6px' }}>Заказчик</th>
                    <th style={{ padding: '8px 6px' }}>Заголовок</th>
                    <th style={{ padding: '8px 6px', minWidth: '120px' }}>Категория</th>
                    <th style={{ padding: '8px 6px' }}>Бюджет</th>
                    <th style={{ padding: '8px 6px' }}>Откл.</th>
                    <th style={{ padding: '8px 6px' }}>Создан</th>
                    <th style={{ padding: '8px 6px' }} />
                  </tr>
                </thead>
                <tbody>
                  {ordersList.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ padding: '12px 6px', color: 'var(--fc-text-muted)' }}>
                        Нет заказов по фильтру.
                      </td>
                    </tr>
                  ) : (
                    ordersList.map((row) => {
                      const cust =
                        [row.customer_first_name, row.customer_last_name].filter(Boolean).join(' ') || '—';
                      const un = row.customer_username ? `@${row.customer_username}` : '';
                      const titleShort =
                        row.title && row.title.length > 48 ? `${row.title.slice(0, 48)}…` : row.title || '—';
                      const canDel = row.status === 'open' || row.status === 'cancelled';
                      const catLabel = row.category != null ? orderCategoryLabel(row.category) : '—';
                      return (
                        <tr
                          key={row.id}
                          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', verticalAlign: 'top' }}
                        >
                          <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                            <strong>{orderRefLabel(row.id)}</strong>
                            {row.is_promo_free ? (
                              <span style={{ display: 'block', fontSize: '10px', color: 'var(--fc-warning)' }}>промо</span>
                            ) : null}
                          </td>
                          <td style={{ padding: '8px 6px', maxWidth: '200px', lineHeight: 1.35 }}>
                            {orderStatusLabel(row.status)}
                          </td>
                          <td style={{ padding: '8px 6px', maxWidth: '140px' }}>
                            <div>{cust}</div>
                            <div style={{ fontSize: '10px', color: 'var(--fc-text-muted)' }}>
                              {un} · uid {row.customer_id}
                            </div>
                          </td>
                          <td style={{ padding: '8px 6px', maxWidth: '200px' }} title={row.title || ''}>
                            {titleShort}
                          </td>
                          <td
                            style={{ padding: '8px 6px', maxWidth: '180px', lineHeight: 1.35, wordBreak: 'break-word' }}
                            title={catLabel}
                          >
                            {catLabel}
                          </td>
                          <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                            {formatMicroUsdt(Number(row.budget) || 0)}
                          </td>
                          <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                            {row.responses_pending_count} / {row.responses_total_count}
                          </td>
                          <td style={{ padding: '8px 6px', whiteSpace: 'nowrap', color: 'var(--fc-text-muted)' }}>
                            {row.created_at
                              ? new Date(row.created_at).toLocaleString('ru-RU', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : '—'}
                          </td>
                          <td style={{ padding: '8px 6px' }}>
                            {canDel ? (
                              <button
                                type="button"
                                disabled={orderRowDeleteBusy === row.id}
                                onClick={() => void deleteOrderFromTable(row)}
                                style={{
                                  padding: '6px 8px',
                                  fontSize: '11px',
                                  borderRadius: '6px',
                                  border: 'none',
                                  background: 'var(--fc-danger)',
                                  color: 'var(--fc-on-danger)',
                                  cursor: orderRowDeleteBusy === row.id ? 'wait' : 'pointer',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {orderRowDeleteBusy === row.id ? '…' : 'Удалить'}
                              </button>
                            ) : (
                              <span style={{ fontSize: '11px', color: 'var(--fc-text-muted)' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          </div>
        ) : null}

        {tab === 'users' ? (
          <div style={panelStyle}>
            <h2 style={{ margin: '0 0 8px', fontSize: '16px', color: 'var(--fc-accent)' }}>Пользователи</h2>
            <p style={{ margin: '0 0 10px', fontSize: '12px', color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
              «Бюджет» — только реальный эскроу (hold), не номинал открытых заказов/цен услуг. CSV — до 5000 строк по фильтру.
            </p>
            {usersErr ? (
              <p style={{ fontSize: '13px', color: 'var(--fc-danger)', margin: '0 0 10px', lineHeight: 1.45 }}>
                {usersErr}
              </p>
            ) : null}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Поиск: @username, имя, фамилия, id, telegram_id"
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '10px',
                  border: '1px solid var(--fc-input-border)',
                  background: 'var(--fc-input-bg)',
                  color: 'var(--fc-input-text)',
                  width: '100%',
                  maxWidth: '340px'
                }}
              />
              <label style={{ fontSize: '13px', color: 'var(--fc-text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Роль
                <select
                  value={usersRoleFilter}
                  onChange={(e) => {
                    const v = e.target.value;
                    setUsersRoleFilter(v);
                    void fetchUsersForPage(secret, 0, v, usersBannedFilter, usersSearch);
                  }}
                  style={{
                    padding: '6px 8px',
                    borderRadius: '8px',
                    border: '1px solid var(--fc-input-border)',
                    background: 'var(--fc-input-bg)',
                    color: 'var(--fc-input-text)',
                  }}
                >
                  <option value="all">Все</option>
                  <option value="customer">Заказчики</option>
                  <option value="executor">Исполнители</option>
                  <option value="pending">pending</option>
                </select>
              </label>
              <label style={{ fontSize: '13px', color: 'var(--fc-text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Бан
                <select
                  value={usersBannedFilter}
                  onChange={(e) => {
                    const v = e.target.value;
                    setUsersBannedFilter(v);
                    void fetchUsersForPage(secret, 0, usersRoleFilter, v, usersSearch);
                  }}
                  style={{
                    padding: '6px 8px',
                    borderRadius: '8px',
                    border: '1px solid var(--fc-input-border)',
                    background: 'var(--fc-input-bg)',
                    color: 'var(--fc-input-text)',
                  }}
                >
                  <option value="all">Все</option>
                  <option value="1">Только забаненные</option>
                  <option value="0">Только не забаненные</option>
                </select>
              </label>
              <button
                type="button"
                disabled={usersLoading}
                onClick={() => void fetchUsersForPage(secret, 0, usersRoleFilter, usersBannedFilter, usersSearch)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid var(--fc-border)',
                  background: 'transparent',
                  color: 'var(--fc-text-muted)',
                  cursor: usersLoading ? 'wait' : 'pointer',
                  fontSize: '12px'
                }}
              >
                Найти
              </button>
              <button
                type="button"
                disabled={usersCsvBusy || !secret}
                onClick={() => void downloadUsersCsv()}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(var(--fc-accent-rgb), 0.4)',
                  background: 'rgba(var(--fc-accent-rgb), 0.14)',
                  color: 'var(--fc-accent)',
                  cursor: usersCsvBusy ? 'wait' : 'pointer',
                  fontSize: '12px'
                }}
              >
                {usersCsvBusy ? 'CSV…' : 'Скачать CSV'}
              </button>
              <span style={{ fontSize: '12px', color: 'var(--fc-text-muted)' }}>
                {usersTotal} всего · стр. {usersPage + 1} / {Math.max(1, Math.ceil(usersTotal / USERS_PAGE_SIZE))}
              </span>
              <button
                type="button"
                disabled={usersLoading || usersPage <= 0}
                onClick={() => void fetchUsersForPage(secret, usersPage - 1, usersRoleFilter, usersBannedFilter, usersSearch)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid var(--fc-border)',
                  background: 'transparent',
                  color: 'var(--fc-text-muted)',
                  cursor: usersPage <= 0 ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
              >
                ←
              </button>
              <button
                type="button"
                disabled={usersLoading || (usersPage + 1) * USERS_PAGE_SIZE >= usersTotal}
                onClick={() => void fetchUsersForPage(secret, usersPage + 1, usersRoleFilter, usersBannedFilter, usersSearch)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid var(--fc-border)',
                  background: 'transparent',
                  color: 'var(--fc-text-muted)',
                  cursor: (usersPage + 1) * USERS_PAGE_SIZE >= usersTotal ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
              >
                →
              </button>
            </div>
            {usersLoading ? (
              <p style={{ color: 'var(--fc-text-muted)', fontSize: '13px' }}>Загрузка…</p>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table
                  style={{
                    width: '100%',
                    minWidth: '1280px',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    color: 'var(--fc-text)'
                  }}
                >
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <th style={{ padding: '8px 6px' }}>ID</th>
                      <th style={{ padding: '8px 6px' }}>TG</th>
                      <th style={{ padding: '8px 6px' }}>Имя</th>
                      <th style={{ padding: '8px 6px' }}>Роль</th>
                      <th style={{ padding: '8px 6px' }}>Баланс</th>
                      <th style={{ padding: '8px 6px' }}>Открыто</th>
                      <th style={{ padding: '8px 6px' }}>Статус</th>
                      <th style={{ padding: '8px 6px' }}>Бюджет</th>
                      <th style={{ padding: '8px 6px' }}>На биржу</th>
                      <th style={{ padding: '8px 6px' }}>С биржи</th>
                      <th style={{ padding: '8px 6px' }}>Регистрация</th>
                      <th style={{ padding: '8px 6px', minWidth: '200px' }}>Привилегии</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersList.length === 0 ? (
                      <tr>
                        <td colSpan={12} style={{ padding: '12px 6px', color: 'var(--fc-text-muted)' }}>
                          Нет пользователей по фильтру.
                        </td>
                      </tr>
                    ) : (
                      usersList.map((u) => {
                        const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
                        const un = u.username ? `@${u.username}` : '';
                        const tid = u.telegram_id != null ? String(u.telegram_id) : '—';
                        const isExecutor = String(u.role || '').trim() === 'executor';
                        const openCount = isExecutor
                          ? Number(u.active_services_count || 0)
                          : Number(u.active_orders_count || 0);
                        const openLabel = isExecutor ? 'услуг' : 'заданий';
                        const budgetMicro = isExecutor
                          ? Number(u.active_services_price_micro || 0)
                          : Number(u.active_orders_budget_micro || 0);
                        const execVipLine =
                          u.executor_vip_until && new Date(u.executor_vip_until) > new Date()
                            ? `до ${new Date(u.executor_vip_until).toLocaleDateString('ru-RU')}`
                            : 'нет';
                        const execTopLine =
                          u.executor_top_until && new Date(u.executor_top_until) > new Date()
                            ? `до ${new Date(u.executor_top_until).toLocaleDateString('ru-RU')}`
                            : 'нет';
                        const miniBtn: React.CSSProperties = {
                          fontSize: '10px',
                          padding: '3px 6px',
                          borderRadius: '6px',
                          border: '1px solid var(--fc-border)',
                          background: 'var(--fc-card-hover)',
                          color: 'var(--fc-text-muted)',
                          cursor: 'pointer'
                        };
                        const regAt = u.exchange_registered_at || u.created_at;
                        const registered = regAt ? new Date(regAt).toLocaleString('ru-RU') : '—';
                        return (
                          <tr key={u.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', verticalAlign: 'top' }}>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                              <strong>{u.id}</strong>
                            </td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                              <div>{tid}</div>
                              <div style={{ fontSize: '10px', color: 'var(--fc-text-muted)' }}>{un || '—'}</div>
                            </td>
                            <td style={{ padding: '8px 6px', maxWidth: '180px' }}>
                              <div>{name}</div>
                              {u.is_banned && u.ban_reason ? (
                                <div style={{ fontSize: '10px', color: 'var(--fc-danger)', marginTop: '4px' }}>
                                  {String(u.ban_reason).slice(0, 120)}
                                </div>
                              ) : null}
                            </td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>{adminRoleLabelRu(u.role)}</td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                              {formatMicroUsdt(u.balance_usdt_micro)} USDT
                            </td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                              {openCount} {openLabel}
                            </td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>{adminAccountStatusRu(u)}</td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                              {formatMicroUsdt(budgetMicro)} USDT
                            </td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                              {formatMicroUsdt(u.deposited_usdt_micro)} USDT
                            </td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                              {formatMicroUsdt(u.withdrawn_usdt_micro)} USDT
                            </td>
                            <td style={{ padding: '8px 6px', whiteSpace: 'nowrap', color: 'var(--fc-text-muted)' }}>{registered}</td>
                            <td style={{ padding: '8px 6px', verticalAlign: 'top' }}>
                              {u.role === 'executor' ? (
                                <div>
                                  <div style={{ fontSize: '10px', color: 'var(--fc-text-muted)', marginBottom: 6 }}>
                                    VIP исп.: <strong>{execVipLine}</strong>
                                    <br />
                                    ТОП: <strong>{execTopLine}</strong>
                                    <br />
                                    Услуги→канал VIP:{' '}
                                    <strong>{u.executor_service_channel_posts_remaining ?? 0}</strong>
                                    {' · '}
                                    купленные посты: <strong>{u.executor_service_channel_post_credits ?? 0}</strong>
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      onClick={() => void adminGrantPrivilege(u.id, 'executor_vip')}
                                    >
                                      +VIP
                                    </button>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      onClick={() => void adminRevokePrivilege(u.id, 'executor_vip')}
                                    >
                                      −VIP
                                    </button>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      onClick={() => void adminGrantPrivilege(u.id, 'executor_top')}
                                    >
                                      +ТОП
                                    </button>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      onClick={() => void adminRevokePrivilege(u.id, 'executor_top')}
                                    >
                                      −ТОП
                                    </button>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      title="Подарить бусты публикации услуг в канал (кредиты, как пакеты 1/5/10)"
                                      onClick={() => void adminGrantServiceChannelCredits(u.id)}
                                    >
                                      +Буст усл.
                                    </button>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      title="Подарить рассылку новых заданий на N дней"
                                      onClick={() => void adminGrantPrivilege(u.id, 'category_alerts')}
                                    >
                                      +Рассылка
                                    </button>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      onClick={() => void adminRevokePrivilege(u.id, 'category_alerts')}
                                    >
                                      −Рассылка
                                    </button>
                                  </div>
                                </div>
                              ) : u.role === 'customer' ? (
                                <div>
                                  <div style={{ fontSize: '10px', color: 'var(--fc-text-muted)', marginBottom: 6 }}>
                                    Срочные слоты:{' '}
                                    <strong>{u.urgent_slots_left != null ? u.urgent_slots_left : '—'}</strong>
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      onClick={() => void adminGrantPrivilege(u.id, 'customer_vip')}
                                    >
                                      +VIP зак.
                                    </button>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      onClick={() => void adminRevokePrivilege(u.id, 'customer_vip')}
                                    >
                                      −VIP зак.
                                    </button>
                                    <button
                                      type="button"
                                      disabled={usersPrivilegeBusy === u.id}
                                      style={miniBtn}
                                      title="Добавить слоты срочных размещений (пакет как у покупки)"
                                      onClick={() => void adminGrantUrgentSlots(u.id)}
                                    >
                                      +Срочн.
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <span style={{ color: 'var(--fc-text-muted)' }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}

        {tab === 'promo' ? (
          <div style={{ marginBottom: 16 }}>
            <div style={panelStyle}>
              <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>📢 Реклама на главной</h2>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>
                {promoImageSpec?.hint_ru ||
                  'Картинка баннера: 1080×480 px (горизонталь ~2.25:1), JPG/PNG/WebP до 2 МБ. Важные элементы — ближе к центру.'}
                {' '}
                Кнопка на баннере (обычно «Перейти») ведёт по ссылке. Срок: дни или бессрочно. Пока идёт показ —
                обратный отсчёт и счётчик кликов; когда срок истекает, в admin-bot приходит уведомление с №, названием
                и числом переходов. Без активных слайдов — дефолты приложения.
              </p>
              {promoErr ? (
                <p style={{ color: 'var(--fc-danger)', fontSize: 13, margin: '0 0 10px' }}>{promoErr}</p>
              ) : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                <button
                  type="button"
                  disabled={promoBusy || promoLoading}
                  onClick={() => void fetchPromoSlides(secret)}
                  style={promoBtn}
                >
                  Обновить
                </button>
                <button
                  type="button"
                  disabled={promoBusy}
                  onClick={() => void seedPromoDefaults(false)}
                  style={promoBtn}
                >
                  Заполнить дефолтами (если пусто)
                </button>
                <button
                  type="button"
                  disabled={promoBusy}
                  onClick={() => void seedPromoDefaults(true)}
                  style={promoBtn}
                >
                  Добавить дефолты
                </button>
                <button
                  type="button"
                  disabled={promoBusy}
                  onClick={() => {
                    setPromoEditId(null);
                    setPromoForm(emptyPromoForm());
                  }}
                  style={promoBtn}
                >
                  Новый слайд
                </button>
              </div>

              <div
                style={{
                  display: 'grid',
                  gap: 8,
                  marginBottom: 16,
                  padding: 12,
                  borderRadius: 12,
                  border: '1px solid var(--fc-border)',
                  background: 'var(--fc-card)'
                }}
              >
                <strong style={{ fontSize: 13 }}>
                  {promoEditId ? `Редактирование #${promoEditId}` : 'Новый слайд'}
                </strong>
                <input
                  className="fc-input"
                  placeholder="Заголовок (необязательно, если есть картинка)"
                  value={promoForm.title}
                  onChange={(e) => setPromoForm((f) => ({ ...f, title: e.target.value }))}
                />
                <input
                  className="fc-input"
                  placeholder="Подзаголовок (необязательно)"
                  value={promoForm.subtitle}
                  onChange={(e) => setPromoForm((f) => ({ ...f, subtitle: e.target.value }))}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <select
                    value={promoForm.tone}
                    onChange={(e) => setPromoForm((f) => ({ ...f, tone: e.target.value }))}
                    style={{ padding: 8, borderRadius: 8 }}
                  >
                    <option value="blue">Синий</option>
                    <option value="violet">Фиолетовый</option>
                    <option value="teal">Бирюзовый</option>
                    <option value="amber">Янтарный</option>
                  </select>
                  <select
                    value={promoForm.audience}
                    onChange={(e) => setPromoForm((f) => ({ ...f, audience: e.target.value }))}
                    style={{ padding: 8, borderRadius: 8 }}
                  >
                    <option value="all">Всем</option>
                    <option value="customer">Заказчикам</option>
                    <option value="executor">Исполнителям</option>
                  </select>
                  <select
                    value={promoForm.duration_days}
                    onChange={(e) => setPromoForm((f) => ({ ...f, duration_days: e.target.value }))}
                    style={{ padding: 8, borderRadius: 8 }}
                    title="Срок показа"
                  >
                    <option value="1">1 день</option>
                    <option value="2">2 дня</option>
                    <option value="3">3 дня</option>
                    <option value="7">7 дней</option>
                    <option value="14">14 дней</option>
                    <option value="30">30 дней</option>
                    <option value="0">Бессрочно</option>
                  </select>
                  <input
                    style={{ width: 72, padding: 8, borderRadius: 8 }}
                    placeholder="Порядок"
                    value={promoForm.sort_order}
                    onChange={(e) => setPromoForm((f) => ({ ...f, sort_order: e.target.value }))}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={promoForm.is_active}
                      onChange={(e) => setPromoForm((f) => ({ ...f, is_active: e.target.checked }))}
                    />
                    Активен
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={promoForm.show_telegram_mark}
                      onChange={(e) =>
                        setPromoForm((f) => ({ ...f, show_telegram_mark: e.target.checked }))
                      }
                    />
                    Значок TG
                  </label>
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--fc-text-muted)' }}>
                    Картинка ({promoImageSpec?.width || 1080}×{promoImageSpec?.height || 480}, до{' '}
                    {promoImageSpec?.max_mb || 2} МБ)
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={promoBusy}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadPromoImageFile(f);
                      e.target.value = '';
                    }}
                  />
                  <input
                    className="fc-input"
                    placeholder="или URL / путь /uploads/home_promo/…"
                    value={promoForm.image_url}
                    onChange={(e) => setPromoForm((f) => ({ ...f, image_url: e.target.value }))}
                  />
                  {promoForm.image_url ? (
                    <img
                      src={
                        /^https?:\/\//i.test(promoForm.image_url)
                          ? promoForm.image_url
                          : `${getApiBaseUrl()}${
                              promoForm.image_url.startsWith('/')
                                ? promoForm.image_url
                                : `/${promoForm.image_url}`
                            }`
                      }
                      alt=""
                      style={{
                        maxWidth: '100%',
                        maxHeight: 120,
                        borderRadius: 10,
                        objectFit: 'cover'
                      }}
                    />
                  ) : null}
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--fc-text-muted)' }}>
                    Кнопка на баннере (обычно «Перейти»)
                  </span>
                  <input
                    className="fc-input"
                    placeholder="Текст кнопки"
                    value={promoForm.btn1_label}
                    onChange={(e) => setPromoForm((f) => ({ ...f, btn1_label: e.target.value }))}
                  />
                  <select
                    value={promoForm.btn1_type}
                    onChange={(e) => setPromoForm((f) => ({ ...f, btn1_type: e.target.value }))}
                    style={{ padding: 8, borderRadius: 8 }}
                  >
                    <option value="external">Внешняя ссылка</option>
                    <option value="customer_create">Создать задание</option>
                    <option value="executor_catalog">Каталог заказов</option>
                    <option value="services">Услуги</option>
                    <option value="orders">Мои заказы</option>
                    <option value="profile">Профиль</option>
                    <option value="none">Нет</option>
                  </select>
                  {promoForm.btn1_type === 'external' ? (
                    <input
                      className="fc-input"
                      placeholder="https://…"
                      value={promoForm.btn1_url}
                      onChange={(e) => setPromoForm((f) => ({ ...f, btn1_url: e.target.value }))}
                    />
                  ) : null}
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--fc-text-muted)' }}>Кнопка 2 (необязательно)</span>
                  <input
                    className="fc-input"
                    placeholder="Текст кнопки"
                    value={promoForm.btn2_label}
                    onChange={(e) => setPromoForm((f) => ({ ...f, btn2_label: e.target.value }))}
                  />
                  <select
                    value={promoForm.btn2_type}
                    onChange={(e) => setPromoForm((f) => ({ ...f, btn2_type: e.target.value }))}
                    style={{ padding: 8, borderRadius: 8 }}
                  >
                    <option value="none">Нет</option>
                    <option value="external">Внешняя ссылка</option>
                    <option value="customer_create">Создать задание</option>
                    <option value="executor_catalog">Каталог заказов</option>
                    <option value="services">Услуги</option>
                    <option value="orders">Мои заказы</option>
                    <option value="profile">Профиль</option>
                  </select>
                  {promoForm.btn2_type === 'external' ? (
                    <input
                      className="fc-input"
                      placeholder="https://…"
                      value={promoForm.btn2_url}
                      onChange={(e) => setPromoForm((f) => ({ ...f, btn2_url: e.target.value }))}
                    />
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled={promoBusy}
                  onClick={() => void savePromoSlide()}
                  style={{
                    ...promoBtn,
                    padding: '10px 14px',
                    fontWeight: 700,
                    background: 'rgba(var(--fc-accent-rgb), 0.2)',
                    borderColor: 'rgba(var(--fc-accent-rgb), 0.45)'
                  }}
                >
                  {promoBusy ? '…' : promoEditId ? 'Сохранить' : 'Создать слайд'}
                </button>
              </div>

              {promoLoading ? (
                <p style={{ color: 'var(--fc-text-muted)', fontSize: 13 }}>Загрузка…</p>
              ) : promoSlides.length === 0 ? (
                <p style={{ color: 'var(--fc-text-muted)', fontSize: 13 }}>
                  Слайдов нет — на главной сейчас дефолты из приложения.
                </p>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {promoSlides.map((s) => (
                    <div
                      key={s.id}
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        border: '1px solid var(--fc-border)',
                        background: s.is_active ? 'var(--fc-card)' : 'rgba(0,0,0,0.15)',
                        opacity: s.is_active ? 1 : 0.7
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                        <div>
                          <strong style={{ fontSize: 14 }}>
                            #{s.id} · {s.title || 'без текста'}
                          </strong>
                          <div style={{ fontSize: 12, color: 'var(--fc-text-muted)', marginTop: 4 }}>
                            {s.tone} · {s.audience} · порядок {s.sort_order}
                            {s.is_active ? ' · ✅ активен' : ' · ⏸ выкл'}
                            {s.duration_days != null
                              ? ` · ${s.duration_days} дн.`
                              : s.ends_at
                                ? ` · до ${new Date(s.ends_at).toLocaleDateString('ru-RU')}`
                                : ' · ∞'}
                          </div>
                          {(() => {
                            const cd = formatPromoCountdown(s.ends_at, promoNowTs);
                            if (!cd && !s.ends_at) {
                              return (
                                <div style={{ fontSize: 12, marginTop: 6, color: 'var(--fc-text-muted)' }}>
                                  ⏱ бессрочно
                                  {' · '}клики: {Number(s.click_count) || 0} (уник. {Number(s.unique_click_count) || 0})
                                </div>
                              );
                            }
                            const ended = cd === 'закончилась';
                            return (
                              <div
                                style={{
                                  fontSize: 12,
                                  marginTop: 6,
                                  color: ended ? 'var(--fc-danger)' : 'var(--fc-usdt, #2bb673)',
                                  fontVariantNumeric: 'tabular-nums'
                                }}
                              >
                                ⏱ {ended ? 'показ закончился' : `осталось ${cd}`}
                                {s.ends_at
                                  ? ` · до ${new Date(s.ends_at).toLocaleString('ru-RU', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}`
                                  : ''}
                                {' · '}клики: {Number(s.click_count) || 0} (уник.{' '}
                                {Number(s.unique_click_count) || 0})
                              </div>
                            );
                          })()}
                          {s.subtitle ? (
                            <div style={{ fontSize: 12, marginTop: 4 }}>{s.subtitle}</div>
                          ) : null}
                          {s.actions?.length ? (
                            <div style={{ fontSize: 11, color: 'var(--fc-text-muted)', marginTop: 4 }}>
                              Кнопки:{' '}
                              {s.actions
                                .map((a) => `${a.label}→${a.cta?.type}${a.cta?.url ? `(${a.cta.url})` : ''}`)
                                .join('; ')}
                            </div>
                          ) : null}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' }}>
                          <button
                            type="button"
                            disabled={promoBusy}
                            style={promoBtn}
                            onClick={() => fillPromoFormFromSlide(s)}
                          >
                            Изменить
                          </button>
                          <button
                            type="button"
                            disabled={promoBusy}
                            style={promoBtn}
                            onClick={() => void togglePromoActive(s.id, !s.is_active)}
                          >
                            {s.is_active ? 'Выкл' : 'Вкл'}
                          </button>
                          <button
                            type="button"
                            disabled={promoBusy}
                            style={promoBtn}
                            onClick={() => void deletePromoSlide(s.id)}
                          >
                            Удалить
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}

        {tab === 'sources' ? (
          <div style={{ marginBottom: 16 }}>
            <div style={panelStyle}>
              <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>🌱 Посевы</h2>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>
                Имя в конце ссылки — латиница, цифры и _. Каждое имя считается отдельно: сколько открыли бота,
                сколько заполнили анкету и попали на биржу.
              </p>
              {acquisitionErr ? (
                <p style={{ color: 'var(--fc-danger)', fontSize: 13, margin: '0 0 10px' }}>{acquisitionErr}</p>
              ) : null}
              <strong style={{ fontSize: 13 }}>Создать ссылку</strong>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'stretch',
                  marginTop: 8,
                  border: '1px solid var(--fc-border)',
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: 'var(--fc-input-bg)'
                }}
              >
                <span
                  style={{
                    flex: '0 0 auto',
                    padding: '10px 8px 10px 10px',
                    fontSize: 12,
                    lineHeight: 1.3,
                    color: 'var(--fc-text-muted)',
                    background: 'var(--fc-card-hover)',
                    userSelect: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {`https://t.me/${acquisitionBot}?start=src_`}
                </span>
                <input
                  value={sourceSlug}
                  onChange={(e) => {
                    const raw = e.target.value.trim().toLowerCase();
                    const fromLink = raw.match(/[?&]start=src_([a-z0-9_]*)/);
                    const body = fromLink ? fromLink[1] : raw.replace(/^src_/, '');
                    setSourceSlug(body.replace(/[^a-z0-9_]/g, '').slice(0, 40));
                    setSourceCopied(false);
                  }}
                  placeholder="devchannel"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label="Имя метки"
                  style={{
                    flex: '1 1 140px',
                    minWidth: 140,
                    border: 'none',
                    outline: 'none',
                    padding: '10px 10px',
                    fontSize: 14,
                    background: 'transparent',
                    color: 'var(--fc-input-text)'
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10, alignItems: 'center' }}>
                <button
                  type="button"
                  disabled={!sourceSlug}
                  style={{ ...promoBtn, opacity: sourceSlug ? 1 : 0.5 }}
                  onClick={() => {
                    const link = `https://t.me/${acquisitionBot}?start=src_${sourceSlug}`;
                    const done = () => {
                      setSourceCopied(true);
                      window.setTimeout(() => setSourceCopied(false), 2000);
                    };
                    if (navigator.clipboard?.writeText) {
                      void navigator.clipboard.writeText(link).then(done).catch(() => setSourceCopied(false));
                    }
                  }}
                >
                  {sourceCopied ? 'Скопировано' : 'Скопировать'}
                </button>
                <button
                  type="button"
                  disabled={acquisitionLoading}
                  onClick={() => void fetchAcquisition(secret)}
                  style={promoBtn}
                >
                  Обновить отчёт
                </button>
              </div>
              {sourceSlug ? (
                <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.4, wordBreak: 'break-all' }}>
                  <code>{`https://t.me/${acquisitionBot}?start=src_${sourceSlug}`}</code>
                </p>
              ) : (
                <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--fc-text-muted)' }}>
                  Начало ссылки уже стоит. Впишите только имя в конце.
                </p>
              )}
            </div>
            <div style={panelStyle}>
              <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>Аналитика</h2>
              {acquisitionLoading ? <p style={{ color: 'var(--fc-text-muted)', fontSize: 13 }}>Загрузка…</p> : null}
              {!acquisitionLoading && acquisitionRows.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--fc-text-muted)' }}>Переходов пока нет.</p>
              ) : null}
              {acquisitionRows.map((row) => (
                <div
                  key={row.source_code}
                  style={{
                    marginBottom: 12,
                    paddingBottom: 8,
                    borderBottom: '1px solid var(--fc-border)'
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{row.source_code}</div>
                  <StatRow label="Открыли бота" value={Number(row.opened) || 0} />
                  <StatRow label="Новые анкеты" value={Number(row.registered) || 0} />
                  <StatRow label="Из них заказчики" value={Number(row.customers) || 0} />
                  <StatRow label="Из них исполнители" value={Number(row.executors) || 0} />
                  <StatRow label="Уже были на бирже" value={Number(row.already) || 0} />
                  <StatRow label="Ещё без анкеты" value={Number(row.pending) || 0} />
                  <div style={{ fontSize: 11, color: 'var(--fc-text-muted)', wordBreak: 'break-all', marginTop: 4 }}>
                    {row.link || `https://t.me/${acquisitionBot}?start=${row.source_code}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {tab === 'mail' ? (
          <div style={{ marginBottom: 16 }}>
            {mailErr ? (
              <p style={{ color: 'var(--fc-danger)', fontSize: 13, margin: '0 0 10px' }}>{mailErr}</p>
            ) : null}
            <div style={panelStyle}>
              <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>Кто не закончил регистрацию</h2>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
                Только они. Сюда входят те, кто открыл бота и не заполнил анкету, и те, кто начал анкету, но не
                выбрал роль. Заказчики и исполнители это сообщение не получат. Под вашим текстом бот сам добавит
                кнопку «✅ Продолжить регистрацию».
              </p>
              <p style={{ margin: '0 0 8px', fontSize: 13 }}>
                Получателей: <strong>{mailCounts ? mailCounts.unfinished : '…'}</strong>
              </p>
              <textarea
                value={mailUnfinishedText}
                onChange={(e) => setMailUnfinishedText(e.target.value)}
                placeholder="Текст сообщения"
                rows={5}
                maxLength={3500}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  border: '1px solid var(--fc-border)',
                  background: 'var(--fc-input-bg)',
                  color: 'var(--fc-input-text)',
                  padding: 10,
                  fontSize: 14,
                  resize: 'vertical'
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={mailBusy != null || !mailUnfinishedText.trim()}
                  style={{ ...promoBtn, opacity: mailBusy != null || !mailUnfinishedText.trim() ? 0.5 : 1 }}
                  onClick={() => void sendMail('unfinished', mailUnfinishedText)}
                >
                  {mailBusy === 'unfinished' ? 'Отправка…' : 'Отправить'}
                </button>
              </div>
              {mailUnfinishedNote ? (
                <p style={{ margin: '10px 0 0', fontSize: 13 }}>{mailUnfinishedNote}</p>
              ) : null}
            </div>
            <div style={panelStyle}>
              <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>Заказчики и исполнители</h2>
              <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.45 }}>
                Только готовые анкеты. Кто не закончил регистрацию, в эту рассылку не входит.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {(
                  [
                    { id: 'all', label: 'Всем' },
                    { id: 'executors', label: 'Только исполнители' },
                    { id: 'customers', label: 'Только заказчики' }
                  ] as Array<{ id: 'all' | 'executors' | 'customers'; label: string }>
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setMailAudience(opt.id)}
                    style={{
                      ...promoBtn,
                      border:
                        mailAudience === opt.id
                          ? '1px solid rgba(var(--fc-accent-rgb), 0.5)'
                          : '1px solid var(--fc-border)',
                      color: mailAudience === opt.id ? 'var(--fc-accent)' : 'var(--fc-text-muted)'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p style={{ margin: '0 0 8px', fontSize: 13 }}>
                Получателей:{' '}
                <strong>
                  {mailCounts
                    ? mailAudience === 'executors'
                      ? mailCounts.executors
                      : mailAudience === 'customers'
                        ? mailCounts.customers
                        : mailCounts.all
                    : '…'}
                </strong>
              </p>
              <textarea
                value={mailRegisteredText}
                onChange={(e) => setMailRegisteredText(e.target.value)}
                placeholder="Текст сообщения"
                rows={5}
                maxLength={3500}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: 10,
                  border: '1px solid var(--fc-border)',
                  background: 'var(--fc-input-bg)',
                  color: 'var(--fc-input-text)',
                  padding: 10,
                  fontSize: 14,
                  resize: 'vertical'
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={mailBusy != null || !mailRegisteredText.trim()}
                  style={{ ...promoBtn, opacity: mailBusy != null || !mailRegisteredText.trim() ? 0.5 : 1 }}
                  onClick={() => void sendMail(mailAudience, mailRegisteredText)}
                >
                  {mailBusy === 'registered' ? 'Отправка…' : 'Отправить'}
                </button>
              </div>
              {mailRegisteredNote ? (
                <p style={{ margin: '10px 0 0', fontSize: 13 }}>{mailRegisteredNote}</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {tab === 'dashboard' && stats?.finance && (
          <div style={panelStyle}>
            <React.Fragment>
              <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-usdt)' }}>Финансы (USDT)</h2>
              <StatRow
                label="Кошелёк Crypto Pay (приложение)"
                value={
                  stats.finance.cryptopay_balance_error
                    ? `ошибка: ${String(stats.finance.cryptopay_balance_error).slice(0, 80)}`
                    : stats.finance.usdt?.cryptopay_app_available != null
                      ? `${stats.finance.usdt.cryptopay_app_available} USDT`
                      : '—'
                }
              />
              <StatRow
                label="Касса платформы (учёт)"
                value={formatFinanceUsdt(
                  stats.finance,
                  'platform_treasury_balance',
                  Number(stats.finance.platform_treasury_balance_usdt_micro ?? stats.finance.platform_treasury_balance_stars) || 0
                )}
              />
              <StatRow
                label="Чистый доход кассы"
                value={formatFinanceUsdt(stats.finance, 'net_profit_platform', Number(stats.finance.net_profit_platform_usdt_micro) || 0)}
              />
              <StatRow
                label="Заморожено (hold, активные заказы)"
                value={formatFinanceUsdt(
                  stats.finance,
                  'escrow_orders_budget',
                  Number(stats.finance.escrow_orders_budget_usdt_micro) || 0
                )}
              />
              <StatRow
                label="Комиссия за всё время"
                value={formatFinanceUsdt(
                  stats.finance,
                  'commission_earned_lifetime',
                  Number(stats.finance.commission_earned_lifetime_usdt_micro ?? stats.finance.commission_earned_lifetime_stars) || 0
                )}
              />
              <StatRow
                label="Выведено с кассы"
                value={formatFinanceUsdt(
                  stats.finance,
                  'commission_withdrawn_recorded',
                  Number(stats.finance.commission_withdrawn_recorded_usdt_micro ?? stats.finance.commission_withdrawn_recorded_stars) || 0
                )}
              />
              <StatRow
                label="Вывод кассы за месяц"
                value={formatFinanceUsdt(
                  stats.finance,
                  'platform_withdrawals_current_month',
                  Number(stats.finance.platform_withdrawals_current_month_usdt_micro) || 0
                )}
              />
              <StatRow
                label="У пользователей всего"
                value={formatFinanceUsdt(
                  stats.finance,
                  'usdt_in_user_balances_total',
                  Number(stats.finance.usdt_in_user_balances_total_micro ?? stats.finance.stars_in_user_balances_total) || 0
                )}
              />
              <StatRow
                label="Пополнения Crypto Pay (всего)"
                value={formatFinanceUsdt(
                  stats.finance,
                  'approximate_total_deposits',
                  Number(stats.finance.approximate_total_deposits_usdt_micro ?? stats.finance.approximate_total_deposits_stars) || 0
                )}
              />
              <StatRow
                label="Пополнения Crypto Pay за месяц"
                value={formatFinanceUsdt(stats.finance, 'deposits_current_month', Number(stats.finance.deposits_current_month_usdt_micro) || 0)}
              />
              <StatRow
                label="Вывод пользователей (всего)"
                value={formatFinanceUsdt(stats.finance, 'withdrawal_completed', Number(stats.finance.withdrawal_completed_usdt_micro) || 0)}
              />
              <StatRow
                label="Вывод пользователей за месяц"
                value={formatFinanceUsdt(
                  stats.finance,
                  'user_withdrawals_current_month',
                  Number(stats.finance.user_withdrawals_current_month_usdt_micro) || 0
                )}
              />
              <StatRow
                label="Заявки на вывод (ожидают)"
                value={formatFinanceUsdt(stats.finance, 'withdrawal_pending', Number(stats.finance.withdrawal_pending_usdt_micro) || 0)}
              />
              {stats.finance.note ? (
                <p style={{ margin: '12px 0 0', fontSize: '11px', color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>
                  {String(stats.finance.note)}
                </p>
              ) : null}
              {stats.finance.reconciliation_hint ? (
                <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>
                  {String(stats.finance.reconciliation_hint)}
                </p>
              ) : null}
            </React.Fragment>
          </div>
        )}

        {tab === 'dashboard' && stats?.finance && (
          <div style={panelStyle}>
            <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-usdt)' }}>Stars (оплаты в Telegram)</h2>
            <StatRow
              label="Баланс бота (Telegram)"
              value={`${
                stats.finance.stars_telegram_bot_balance ??
                stats.finance.stars?.telegram_bot_balance ??
                '—'
              } ⭐`}
            />
            <StatRow
              label="Всего поступлений (Telegram)"
              value={`${
                stats.finance.stars_telegram_inbound_lifetime ??
                stats.finance.stars?.telegram_inbound_lifetime ??
                '—'
              } ⭐`}
            />
            <StatRow
              label="Поступлений за этот месяц"
              value={`${
                stats.finance.stars_telegram_inbound_month ??
                stats.finance.stars?.telegram_inbound_month ??
                '—'
              } ⭐`}
            />
            <StatRow
              label="Платежей за месяц"
              value={String(
                stats.finance.stars_telegram_payments_month ??
                  stats.finance.stars?.telegram_payments_month ??
                  '—'
              )}
            />
          </div>
        )}

        {monthlyRows.length > 0 && (
          <div style={panelStyle}>
            <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-usdt)' }}>Архив по месяцам</h2>
            <p style={{ fontSize: '12px', color: 'var(--fc-text-muted)', marginBottom: '10px' }}>
              Границы месяца — по времени сервера БД. USDT.
            </p>
            <div style={{ overflowX: 'auto', fontSize: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--fc-text)' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <th style={{ padding: '6px 4px' }}>Месяц</th>
                    <th style={{ padding: '6px 4px' }}>Пополн.</th>
                    <th style={{ padding: '6px 4px' }}>Вывод польз.</th>
                    <th style={{ padding: '6px 4px' }}>Комиссия</th>
                    <th style={{ padding: '6px 4px' }}>Вывод кассы</th>
                    <th style={{ padding: '6px 4px' }}>Нетто кассы</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyRows.map((row) => (
                    <tr key={row.label} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '6px 4px', whiteSpace: 'nowrap' }}>{row.label}</td>
                      <td style={{ padding: '6px 4px' }}>{row.deposits_usdt ?? '—'}</td>
                      <td style={{ padding: '6px 4px' }}>{row.user_withdrawals_usdt ?? '—'}</td>
                      <td style={{ padding: '6px 4px' }}>{row.commission_usdt ?? '—'}</td>
                      <td style={{ padding: '6px 4px' }}>{row.platform_withdrawals_usdt ?? '—'}</td>
                      <td style={{ padding: '6px 4px' }}>{row.net_platform_usdt ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'dashboard' && stats?.moderation && (
          <div style={panelStyle}>
            <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-accent)' }}>Жалобы</h2>
            <StatRow label="Всего" value={stats.moderation.reports_total ?? 0} />
            <StatRow label="Открыты" value={stats.moderation.reports_open ?? 0} />
          </div>
        )}

        {tab === 'users' && (
        <div style={panelStyle}>
          <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-accent)' }}>Управление XP</h2>
          <p style={{ fontSize: '12px', color: 'var(--fc-text-muted)', marginBottom: '12px', lineHeight: 1.45 }}>
            Начислить или списать XP. Пользователю в основном боте придёт сообщение с новым балансом и комментарием
            (курсивом), если поле заполнено.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setXpDirection('increase')}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: xpDirection === 'increase' ? '1px solid var(--fc-accent)' : '1px solid var(--fc-border)',
                background: xpDirection === 'increase' ? 'rgba(var(--fc-accent-rgb), 0.16)' : 'var(--fc-card-hover)',
                color: xpDirection === 'increase' ? 'var(--fc-accent)' : 'var(--fc-text-muted)',
                cursor: 'pointer'
              }}
            >
              Увеличить
            </button>
            <button
              type="button"
              onClick={() => setXpDirection('decrease')}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: xpDirection === 'decrease' ? '1px solid var(--fc-accent)' : '1px solid var(--fc-border)',
                background: xpDirection === 'decrease' ? 'rgba(var(--fc-accent-rgb), 0.16)' : 'var(--fc-card-hover)',
                color: xpDirection === 'decrease' ? 'var(--fc-accent)' : 'var(--fc-text-muted)',
                cursor: 'pointer'
              }}
            >
              Уменьшить
            </button>
          </div>
          <input
            type="text"
            placeholder="@username или id"
            value={xpUsername}
            onChange={(e) => setXpUsername(e.target.value)}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid var(--fc-input-border)',
              background: 'var(--fc-input-bg)',
              color: 'var(--fc-input-text)',
              marginBottom: '8px',
              boxSizing: 'border-box'
            }}
          />
          <input
            type="text"
            inputMode="numeric"
            placeholder="Количество XP"
            value={xpAmount}
            onChange={(e) => setXpAmount(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid var(--fc-input-border)',
              background: 'var(--fc-input-bg)',
              color: 'var(--fc-input-text)',
              marginBottom: '8px',
              boxSizing: 'border-box'
            }}
          />
          <textarea
            placeholder="Комментарий пользователю (необязательно)"
            value={xpComment}
            onChange={(e) => setXpComment(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid var(--fc-input-border)',
              background: 'var(--fc-input-bg)',
              color: 'var(--fc-input-text)',
              marginBottom: '12px',
              resize: 'vertical' as const,
              boxSizing: 'border-box',
              fontFamily: 'inherit'
            }}
          />
          <button
            type="button"
            disabled={xpBusy}
            onClick={() => void postXpAdjust()}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              background: xpBusy ? 'var(--fc-border-strong)' : 'var(--fc-accent)',
              color: 'var(--fc-on-accent)',
              fontWeight: 600,
              cursor: xpBusy ? 'not-allowed' : 'pointer'
            }}
          >
            {xpBusy ? '…' : 'Применить'}
          </button>
        </div>
        )}

        {tab === 'orders' && (
        <div style={panelStyle}>
          <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-accent)' }}>Жалобы (список)</h2>
          <p style={{ fontSize: '12px', color: 'var(--fc-text-muted)', marginBottom: '10px', lineHeight: 1.4 }}>
            Откройте чат заказа по кнопке или введите ID заказа ниже. Вердикт — после просмотра переписки.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setReportsTab('open')}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: reportsTab === 'open' ? '1px solid var(--fc-accent)' : '1px solid var(--fc-border)',
                background: reportsTab === 'open' ? 'rgba(var(--fc-accent-rgb), 0.15)' : 'var(--fc-card-hover)',
                color: reportsTab === 'open' ? 'var(--fc-accent)' : 'var(--fc-text-muted)',
                cursor: 'pointer'
              }}
            >
              Активные
            </button>
            <button
              type="button"
              onClick={() => setReportsTab('closed')}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: reportsTab === 'closed' ? '1px solid var(--fc-accent)' : '1px solid var(--fc-border)',
                background: reportsTab === 'closed' ? 'rgba(var(--fc-accent-rgb), 0.15)' : 'var(--fc-card-hover)',
                color: reportsTab === 'closed' ? 'var(--fc-accent)' : 'var(--fc-text-muted)',
                cursor: 'pointer'
              }}
            >
              Архив
            </button>
            <button
              type="button"
              onClick={() => void fetchReports(secret, reportsTab)}
              disabled={reportsLoading}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid var(--fc-border)',
                background: 'var(--fc-card-hover)',
                color: 'var(--fc-text-muted)',
                cursor: 'pointer'
              }}
            >
              {reportsLoading ? '…' : 'Обновить'}
            </button>
          </div>
          {reports.length === 0 && !reportsLoading ? (
            <p style={{ fontSize: '13px', color: 'var(--fc-text-muted)' }}>Нет записей</p>
          ) : (
            reports.map((r) => (
              <div
                key={r.id}
                style={{
                  padding: '10px',
                  marginBottom: '8px',
                  background: 'var(--fc-surface-2)',
                  borderRadius: '8px',
                  border: '1px solid rgba(var(--fc-accent-rgb), 0.12)',
                  fontSize: '13px'
                }}
              >
                <div style={{ color: 'var(--fc-text-muted)', marginBottom: '4px' }}>
                  <strong style={{ color: 'var(--fc-accent)' }}>#{r.id}</strong>
                  {r.order_id != null ? ` · заказ ${orderRefLabel(r.order_id)}` : ''} · {r.status || 'open'}
                  {r.kind === 'dispute' ? (
                    <span style={{ marginLeft: '8px', color: 'var(--fc-warning)' }}>⚖️ спор</span>
                  ) : null}
                </div>
                <div style={{ marginBottom: '6px' }}>
                  {[r.reporter_first_name, r.reporter_last_name].filter(Boolean).join(' ') || '—'} →{' '}
                  {[r.reported_first_name, r.reported_last_name].filter(Boolean).join(' ') || '—'}
                </div>
                <div style={{ color: 'var(--fc-text-muted)', marginBottom: '8px' }}>{String(r.reason || '').slice(0, 300)}</div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {r.order_id != null ? (
                    <button
                      type="button"
                      onClick={() => void loadDispute(secret, r.order_id as number)}
                      style={{
                        padding: '6px 10px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'var(--fc-accent)',
                        color: 'var(--fc-on-accent)',
                        cursor: 'pointer'
                      }}
                    >
                      Чат заказа {orderRefLabel(r.order_id)}
                    </button>
                  ) : null}
                  {reportsTab === 'open' ? (
                    <button
                      type="button"
                      onClick={() => void closeReportById(r.id)}
                      disabled={moderationBusy === r.id}
                      style={{
                        padding: '6px 10px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        border: '1px solid var(--fc-border)',
                        background: 'transparent',
                        color: 'var(--fc-text-muted)',
                        cursor: 'pointer'
                      }}
                    >
                      Закрыть жалобу
                    </button>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
        )}

        {tab === 'orders' && (
        <div style={panelStyle}>
          <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-danger)' }}>Удалить заказ</h2>
          <p style={{ fontSize: '12px', color: 'var(--fc-text-muted)', marginBottom: '10px', lineHeight: 1.45 }}>
            Спам, мошенничество, незаконные формулировки. Доступно для статусов <strong>open</strong> и{' '}
            <strong>cancelled</strong>. Для заказа в работе или с эскроу сначала в блоке ниже выполните{' '}
            <em>refund_customer</em>, затем удалите запись.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
            <input
              type="text"
              inputMode="text"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="ID или FRL-0029"
              value={deleteOrderInput}
              onChange={(e) => setDeleteOrderInput(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid var(--fc-input-border)',
                background: 'var(--fc-input-bg)',
                color: 'var(--fc-input-text)',
                width: '160px'
              }}
            />
            <label style={{ fontSize: '13px', color: 'var(--fc-text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="checkbox"
                checked={deleteBanCustomer}
                onChange={(e) => setDeleteBanCustomer(e.target.checked)}
              />
              Забанить заказчика
            </label>
            <button
              type="button"
              disabled={deleteBusy}
              onClick={() => void postDeleteOrder()}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--fc-danger)',
                color: 'var(--fc-on-danger)',
                fontWeight: 600,
                cursor: deleteBusy ? 'wait' : 'pointer'
              }}
            >
              {deleteBusy ? '…' : 'Удалить заказ'}
            </button>
          </div>
          <p style={{ fontSize: '11px', color: 'var(--fc-text-muted)', margin: 0 }}>
            При создании заданий действует автофильтр по тексту (взлом, наркотики и т.д.); полный бан автора — опция{' '}
            <code style={{ color: 'var(--fc-accent)' }}>ORDER_MODERATION_AUTO_BAN=1</code> на сервере.
          </p>
        </div>
        )}

        {tab === 'orders' && (
        <div style={panelStyle}>
          <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-warning)' }}>Спор по заказу</h2>
          <p style={{ fontSize: '12px', color: 'var(--fc-text-muted)', marginBottom: '8px' }}>
            Ссылка из бота: <code style={{ color: 'var(--fc-accent)' }}>#admin?order=ID</code> или FRL-номер
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              const oid = String(fd.get('order_id') || '').trim();
              if (oid) void loadDispute(secret, oid);
            }}
            style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}
          >
            <input
              name="order_id"
              placeholder="ID заказа"
              defaultValue={disputeOrderId != null ? String(disputeOrderId) : ''}
              style={{
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid var(--fc-input-border)',
                background: 'var(--fc-input-bg)',
                color: 'var(--fc-input-text)',
                width: '120px'
              }}
            />
            <button
              type="submit"
              disabled={disputeLoading}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--fc-warning)',
                color: 'var(--fc-on-warning)',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Загрузить чат
            </button>
          </form>
          {disputeLoading ? (
            <p style={{ color: 'var(--fc-text-muted)' }}>Загрузка…</p>
          ) : disputeData && disputeOrderId != null ? (
            <>
              <div style={{ fontSize: '13px', marginBottom: '10px' }}>
                <strong>Заказ {orderRefLabel(disputeOrderId)}</strong> ·{' '}
                {String((disputeData.order as { status?: string }).status ?? '')} · промо:{' '}
                {(disputeData.order as { is_promo_free?: boolean }).is_promo_free ? 'да' : 'нет'}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--fc-text-muted)', marginBottom: '10px' }}>
                {(disputeData as { warning?: string }).warning}
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {disputeData.customer ? (
                  <div style={{ flex: '1 1 200px', background: 'var(--fc-surface-2)', padding: '10px', borderRadius: '8px' }}>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>Заказчик</div>
                    <div>
                      @{disputeData.customer.username || '—'} · trust {disputeData.customer.trust_score ?? '—'} · XP{' '}
                      {disputeData.customer.score_balance ?? '—'}
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => void applyTrustDelta(disputeData.customer!.id, -5)}
                        disabled={moderationBusy === disputeData.customer!.id}
                        style={{ fontSize: '11px', padding: '4px 8px', cursor: 'pointer' }}
                      >
                        trust −5
                      </button>
                      <button
                        type="button"
                        onClick={() => void banUserById(disputeData.customer!.id)}
                        disabled={moderationBusy === disputeData.customer!.id}
                        style={{ fontSize: '11px', padding: '4px 8px', cursor: 'pointer' }}
                      >
                        Бан
                      </button>
                    </div>
                  </div>
                ) : null}
                {disputeData.executor ? (
                  <div style={{ flex: '1 1 200px', background: 'var(--fc-surface-2)', padding: '10px', borderRadius: '8px' }}>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>Исполнитель</div>
                    <div>
                      @{disputeData.executor.username || '—'} · trust {disputeData.executor.trust_score ?? '—'} · XP{' '}
                      {disputeData.executor.score_balance ?? '—'}
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => void applyTrustDelta(disputeData.executor!.id, -5)}
                        disabled={moderationBusy === disputeData.executor!.id}
                        style={{ fontSize: '11px', padding: '4px 8px', cursor: 'pointer' }}
                      >
                        trust −5
                      </button>
                      <button
                        type="button"
                        onClick={() => void banUserById(disputeData.executor!.id)}
                        disabled={moderationBusy === disputeData.executor!.id}
                        style={{ fontSize: '11px', padding: '4px 8px', cursor: 'pointer' }}
                      >
                        Бан
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--fc-text-muted)', marginBottom: '6px' }}>
                Комментарий к проводкам/вердикту (опционально)
              </label>
              <textarea
                value={verdictNote}
                onChange={(e) => setVerdictNote(e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  padding: '8px',
                  borderRadius: '8px',
                  border: '1px solid var(--fc-input-border)',
                  background: 'var(--fc-input-bg)',
                  color: 'var(--fc-input-text)',
                  marginBottom: '10px',
                  fontSize: '13px'
                }}
              />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <button
                  type="button"
                  onClick={() => void postDisputeResolve('refund_customer')}
                  disabled={moderationBusy === disputeOrderId}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'var(--fc-accent)',
                    color: 'var(--fc-on-accent)',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Возврат заказчику / отмена
                </button>
                <button
                  type="button"
                  onClick={() => void postDisputeResolve('pay_executor')}
                  disabled={moderationBusy === disputeOrderId}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'var(--fc-usdt)',
                    color: 'var(--fc-on-usdt)',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Выплатить исполнителю
                </button>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {String((disputeData.order as { status?: string }).status ?? '') === 'in_progress' &&
                disputeData.executor ? (
                  <button
                    type="button"
                    onClick={() => void postRemoveExecutor()}
                    disabled={disputeExtraBusy || moderationBusy !== null}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--fc-danger)',
                      background: 'rgba(var(--fc-danger-rgb), 0.16)',
                      color: 'var(--fc-danger)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '12px'
                    }}
                  >
                    Снять исполнителя (в ленту)
                  </button>
                ) : null}
                {disputeData.customer ? (
                  <button
                    type="button"
                    onClick={() => void postParticipantXpPenalty('customer')}
                    disabled={disputeExtraBusy || moderationBusy !== null}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--fc-warning)',
                      background: 'rgba(var(--fc-warning-rgb), 0.16)',
                      color: 'var(--fc-warning)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '12px'
                    }}
                  >
                    XP −20 заказчик
                  </button>
                ) : null}
                {disputeData.executor ? (
                  <button
                    type="button"
                    onClick={() => void postParticipantXpPenalty('executor')}
                    disabled={disputeExtraBusy || moderationBusy !== null}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid var(--fc-warning)',
                      background: 'rgba(var(--fc-warning-rgb), 0.16)',
                      color: 'var(--fc-warning)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '12px'
                    }}
                  >
                    XP −20 исполнитель
                  </button>
                ) : null}
              </div>
              <div style={{ maxHeight: '280px', overflowY: 'auto', fontSize: '12px', lineHeight: 1.45 }}>
                {disputeData.messages.map((m) => {
                  const who = m.sender_username
                    ? `@${m.sender_username}`
                    : `id ${m.sender_telegram_id ?? m.sender_id}`;
                  return (
                    <div
                      key={m.id}
                      style={{
                        padding: '8px 0',
                        borderBottom: '1px solid rgba(255,255,255,0.06)'
                      }}
                    >
                      <span style={{ color: 'var(--fc-accent)' }}>{who}</span>{' '}
                      <span style={{ color: 'var(--fc-text-muted)' }}>{new Date(m.created_at).toLocaleString('ru-RU')}</span>
                      <div style={{ color: 'var(--fc-text)', marginTop: '4px', whiteSpace: 'pre-wrap' }}>{m.body}</div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <p style={{ fontSize: '13px', color: 'var(--fc-text-muted)' }}>Укажите ID заказа или откройте ссылку из уведомления.</p>
          )}
        </div>
        )}

        {tab === 'users' && (
        <div style={panelStyle}>
          <h2 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--fc-warning)' }}>
            💸 Заявки на вывод USDT
          </h2>
          <p style={{ fontSize: '12px', color: 'var(--fc-text-muted)', marginBottom: '12px', lineHeight: 1.4 }}>
            Отправьте USDT (например @CryptoBot transfer) → «Выполнить». Отклонить — вернёт на баланс биржи.
          </p>
          <button
            type="button"
            onClick={() => void fetchWithdrawals(secret)}
            disabled={withdrawalsLoading}
            style={{
              padding: '8px 14px',
              fontSize: '13px',
              borderRadius: '8px',
              border: '1px solid var(--fc-border)',
              background: 'var(--fc-card-hover)',
              color: 'var(--fc-text-muted)',
              cursor: 'pointer',
              marginBottom: '12px'
            }}
          >
            {withdrawalsLoading ? '…' : 'Обновить'}
          </button>
          {withdrawals.length === 0 ? (
            <p style={{ fontSize: '13px', color: 'var(--fc-text-muted)' }}>Нет заявок в обработке</p>
          ) : (
            withdrawals.map((w) => (
              <div
                key={w.id}
                style={{
                  padding: '12px',
                  marginBottom: '10px',
                  background: 'var(--fc-surface-2)',
                  borderRadius: '10px',
                  border: '1px solid rgba(255,183,77,0.2)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                  <div>
                    <strong style={{ color: 'var(--fc-warning)' }}>#{w.id}</strong> ·{' '}
                    {formatMicroUsdt(w.amount_usdt_micro ?? w.amount_stars)} USDT
                    <div style={{ fontSize: '12px', color: 'var(--fc-text-muted)', marginTop: '4px' }}>
                      {w.username ? `@${w.username}` : ''} {w.first_name || ''} {w.last_name || ''}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--fc-text-muted)', marginTop: '2px' }}>
                      tg id: {String(w.telegram_id)} · {w.created_at ? new Date(w.created_at).toLocaleString('ru-RU') : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => completeWithdrawal(w.id)}
                      disabled={actionBusy === w.id}
                      style={{
                        padding: '8px 12px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        border: 'none',
                        background: 'var(--fc-usdt)',
                        color: 'var(--fc-on-usdt)',
                        cursor: actionBusy === w.id ? 'wait' : 'pointer',
                        fontWeight: 600
                      }}
                    >
                      {actionBusy === w.id ? '…' : 'Выполнить'}
                    </button>
                    <button
                      type="button"
                      onClick={() => rejectWithdrawal(w.id)}
                      disabled={actionBusy === w.id}
                      style={{
                        padding: '8px 12px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        border: '1px solid var(--fc-danger)',
                        background: 'transparent',
                        color: 'var(--fc-danger)',
                        cursor: actionBusy === w.id ? 'wait' : 'pointer'
                      }}
                    >
                      Отклонить
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        )}

        {stats?.config && (
          <p style={{ fontSize: '11px', color: 'var(--fc-text-muted)', marginTop: '8px' }}>
            Комиссия в конфиге: {stats.config.platform_commission_percent}% · platform_telegram_id:{' '}
            {stats.config.platform_telegram_id}
          </p>
        )}

        <button
          type="button"
          onClick={() => {
            void fetchStats(secret);
            void fetchWithdrawals(secret);
            void fetchMonthlyArchive(secret);
            void fetchReports(secret, reportsTab);
          }}
          disabled={loading}
          style={{
            marginTop: '8px',
            padding: '10px 20px',
            borderRadius: '10px',
            border: '1px solid var(--fc-border)',
            background: 'var(--fc-card-hover)',
            color: 'var(--fc-text)',
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Обновить данные
        </button>
      </header>
    </div>
  );
}
